'use client';
import { prepareCaptchaBody } from '@eliadmin/core/src/api/recaptcha-client';
import { CaptchaAction } from '@eliadmin/core/src/api/recaptcha-actions';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  Download,
  Minus,
  Plus,
  Send,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Toaster } from '@eliadmin/core/src/components/ui/toaster';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { PlatePreview } from './plate-preview';
import { createBendMeshDebugReport } from './plate-preview-3d';
import { ProductTypePreview3D } from './product-type-preview-3d';
import { openDxfSheetDocument, openTechnicalDrawingDocument } from './lib/configurator-documents';
import {
  getConfiguratorData,
  getConfiguratorDataForProduct,
  setConfiguratorData,
  type ConfiguratorData,
  type RuntimeConfiguratorData,
  type StoreDashboardConfiguration,
} from './lib/configurator-data';
import {
  buildProductPatch,
  clampToLimit,
  formatNumber,
  formatWeight,
  getEffectiveLimits,
  getHoleLayout,
  getProductBendLines,
  getProductInternalCuts,
  getProductOutlineNotches,
  getProductOutlineSettings,
  getProductOptions,
  getSelectedOptions,
  type BendDirection,
  type BendLineOverride,
  type BendOrientation,
  type BendPositionConstraint,
  type BendPositionMode,
  type HoleShape,
  type InternalCutOverride,
  type HoleSlotOrientation,
  type OutlineNotchEdge,
  type OutlineNotchOverride,
  productSupportsFeature,
  type ConfiguratorState,
} from './lib/configurator-math';
import { calculateResolvedEstimate, resolvePartGeometry } from './lib/part-geometry';

type Screen = 'dashboard' | 'flow';
type FlowMode = 'new' | 'draft' | 'submitted';
type ViewMode = '2d' | '3d';

const CONFIGURATOR_STEPS = [
  { id: 1, label: 'Typ výrobku' },
  { id: 2, label: 'Typ stroja' },
  { id: 3, label: 'Parametre' },
  { id: 4, label: 'text4085' },
  { id: 5, label: 'Potvrdenie' },
] as const;
const CONFIGURATOR_STEP_DETAILS: Record<number, { title: string; subtitle: string }> = {
  1: {
    title: 'Vyberte typ výrobku',
    subtitle: 'Začneme tým najdôležitejším - povedzte nám, aký výrobok si želáte konfigurovať.',
  },
  2: {
    title: 'Vyberte typ stroja',
    subtitle: 'Vyberte preferovaný stroj zo zoznamu.',
  },
  3: {
    title: 'Parametre výrobku',
    subtitle: '',
  },
  4: {
    title: 'text4085',
    subtitle: 'Vyplňte údaje a skontrolujte parametre pred odoslaním.',
  },
  5: {
    title: 'Potvrdenie',
    subtitle: 'text4086',
  },
};
const SUBMITTED_SUMMARY_STEP_DETAILS = {
  title: 'text4089',
  subtitle: 'Konfigurácia už bola odoslaná. Uložením upravíte existujúcu konfiguráciu.',
};
const CONFIGURATOR_HERO = {
  title: 'Konfigurácia',
  subtitle:
    'Vytvorte si konfiguráciu kovového dielca pre rezanie, vŕtanie, ohýbanie alebo povrchovú úpravu.',
};
const SHOW_CONFIGURATOR_DEBUG = process.env.NEXT_PUBLIC_CONFIGURATOR_DEBUG === 'true';
type LocalizedValue = string | Record<string, string | null | undefined> | null | undefined;
type DashboardItem = {
  id: string;
  title: string;
  subtitle: string;
  step?: string;
  status?: string;
  statusTone?: 'neutral' | 'success';
  configuration: StoreDashboardConfiguration;
};
type DashboardState = {
  drafts: DashboardItem[];
  quotes: DashboardItem[];
  isLoading: boolean;
  error: string | null;
};
type QuotePurchaseType = 'person' | 'company';
type QuoteCustomerData = {
  purchaseType: QuotePurchaseType;
  companyIco: string;
  companyName: string;
  companyDic: string;
  companyIcDph: string;
  address: string;
  streetNumber: string;
  city: string;
  postcode: string;
  billingAddress: string;
  contactName: string;
  email: string;
  phone: string;
  deliveryDate: string;
  deliveryMethod: string;
  note: string;
};
type StoredConfiguratorConfiguration = StoreDashboardConfiguration & {
  id?: number | string;
  customer_data?: Record<string, unknown> | null;
  currency?: string | null;
  total_price_with_vat?: string | number | null;
};

const STORE_CONFIGURATION_STATUS_DRAFT = 'draft';
const STORE_CONFIGURATION_STATUS_CREATED = 'created';
type StoreCreatableConfigurationStatus =
  typeof STORE_CONFIGURATION_STATUS_DRAFT | typeof STORE_CONFIGURATION_STATUS_CREATED;

const DRAFT_CONFIGURATION_STATUSES = new Set([
  STORE_CONFIGURATION_STATUS_DRAFT,
  'in_progress',
  'in-progress',
  'unfinished',
]);
const FINISHED_CONFIGURATION_STATUSES = new Set([
  'done',
  'completed',
  'finished',
  'approved',
  'accepted',
  'rejected',
  'resolved',
]);
const SUBMITTED_CONFIGURATION_STATUSES = new Set([
  STORE_CONFIGURATION_STATUS_CREATED,
  'submitted',
  'sent',
  'pending',
  ...FINISHED_CONFIGURATION_STATUSES,
]);
const PRODUCT_CONFIGURATOR_SLUGS: Record<string, string> = {
  'lisonex-bent-slot-link': 'skladana-spojka',
  'lisonex-upinka-01-three': 'upinka',
  'lisonex-base-blank-plate': 'zakladna-platna',
};
const EMPTY_QUOTE_CUSTOMER_DATA: QuoteCustomerData = {
  purchaseType: 'person',
  companyIco: '',
  companyName: '',
  companyDic: '',
  companyIcDph: '',
  address: '',
  streetNumber: '',
  city: '',
  postcode: '',
  billingAddress: '',
  contactName: '',
  email: '',
  phone: '',
  deliveryDate: '',
  deliveryMethod: '',
  note: '',
};

function resolveLocalizedValue(value: LocalizedValue) {
  if (!value) return '';
  if (typeof value === 'string') return value;

  return value.sk ?? value.cs ?? value.en ?? Object.values(value).find(Boolean) ?? '';
}

function formatDashboardDate(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('sk-SK', {
    day: 'numeric',
    month: 'long',
  }).format(date);
}

function getDashboardApiUrl() {
  return getStoreApiUrl('/store/product-configurators/configurations');
}

function getStoreApiUrl(path: string) {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;

  const rawBaseUrl =
    process.env.NEXT_PUBLIC_API_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const baseUrl = rawBaseUrl.replace(/\/+$/, '').replace(/\/api$/, '');
  const storePath = path.replace(/^\/api\//, '/').replace(/^\/+/, '');

  return `${baseUrl}/${storePath}`;
}

function getStoreHeaders(authToken?: string | null, locale?: string, hasJsonBody = false) {
  return {
    Accept: 'application/json',
    ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    'Current-Store': process.env.NEXT_PUBLIC_CURRENT_STORE ?? '',
    ...(locale ? { 'Accept-Language': locale } : {}),
  };
}

function splitContactName(contactName: string) {
  const parts = contactName.trim().split(/\s+/).filter(Boolean);

  return {
    name: parts.shift() ?? '',
    surname: parts.join(' '),
  };
}

function emptyToNull(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function serializeQuoteCustomerData(customerData: QuoteCustomerData) {
  const isCompany = customerData.purchaseType === 'company';
  const splitName = splitContactName(customerData.contactName);

  return {
    company_buy: isCompany,
    address: customerData.billingAddress.trim() || customerData.address.trim(),
    street_number: customerData.streetNumber.trim(),
    city: customerData.city.trim(),
    postcode: customerData.postcode.trim(),
    name: splitName.name,
    surname: splitName.surname,
    email: customerData.email.trim(),
    phone: customerData.phone.trim(),
    expected_delivery_date: customerData.deliveryDate.trim(),
    delivery_method: customerData.deliveryMethod.trim(),
    note: customerData.note.trim(),
    ico: isCompany ? emptyToNull(customerData.companyIco) : null,
    company: isCompany ? emptyToNull(customerData.companyName) : null,
    dic: isCompany ? emptyToNull(customerData.companyDic) : null,
    ic_dph: isCompany ? emptyToNull(customerData.companyIcDph) : null,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readRecordString(source: Record<string, unknown> | null | undefined, keys: string[]) {
  if (!source) return '';

  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }

  return '';
}

function readRecordBoolean(source: Record<string, unknown> | null | undefined, keys: string[]) {
  if (!source) return false;

  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['1', 'true', 'yes', 'company'].includes(normalized)) return true;
      if (['0', 'false', 'no', 'person'].includes(normalized)) return false;
    }
  }

  return false;
}

function getQuoteCustomerDataFromStoredConfiguration(
  configuration: StoreDashboardConfiguration | null | undefined,
): QuoteCustomerData {
  const source = asRecord(configuration?.customer_data);
  if (!source) return EMPTY_QUOTE_CUSTOMER_DATA;

  const purchaseType = readRecordString(source, ['purchaseType', 'purchase_type', 'customer_kind']);
  const isCompany =
    purchaseType === 'company' ||
    readRecordBoolean(source, ['company_buy', 'isCompany', 'is_company']);

  return {
    ...EMPTY_QUOTE_CUSTOMER_DATA,
    purchaseType: isCompany ? 'company' : 'person',
    companyIco: readRecordString(source, ['companyIco', 'company_ico', 'ico']),
    companyName: readRecordString(source, ['companyName', 'company_name', 'company']),
    companyDic: readRecordString(source, ['companyDic', 'company_dic', 'dic']),
    companyIcDph: readRecordString(source, ['companyIcDph', 'company_ic_dph', 'ic_dph']),
    address: readRecordString(source, ['address', 'billingAddress', 'billing_address']),
    streetNumber: readRecordString(source, ['street_number', 'streetNumber']),
    city: readRecordString(source, ['city']),
    postcode: readRecordString(source, ['postcode', 'postal_code', 'zip']),
    billingAddress: readRecordString(source, [
      'billingAddress',
      'billing_address',
      'address',
      'company_address',
    ]),
    contactName:
      readRecordString(source, ['contactName', 'contact_name', 'company_contact_name']) ||
      [readRecordString(source, ['name']), readRecordString(source, ['surname'])]
        .filter(Boolean)
        .join(' '),
    email: readRecordString(source, ['email', 'company_mail', 'contact_email']),
    phone: readRecordString(source, ['phone', 'company_phone', 'contact_phone']),
    deliveryDate: readRecordString(source, [
      'deliveryDate',
      'delivery_date',
      'expected_delivery_date',
    ]),
    deliveryMethod: readRecordString(source, ['deliveryMethod', 'delivery_method']),
    note: readRecordString(source, ['note', 'message']),
  };
}

function customerExtraString(customer: Customer, key: string) {
  const value = (customer as unknown as Record<string, unknown>)[key];
  return typeof value === 'string' ? value.trim() : '';
}

function joinAddressParts(parts: Array<string | null | undefined>) {
  return parts
    .map(part => part?.trim())
    .filter(Boolean)
    .join(', ');
}

function getQuoteCustomerDataFromCustomer(customer: Customer): QuoteCustomerData {
  const personalName = [customer.name, customer.surname]
    .map(value => value?.trim())
    .filter(Boolean)
    .join(' ');
  const personalAddress = joinAddressParts([
    customer.address,
    customer.street_number,
    customer.postcode,
    customer.city,
  ]);
  const companyAddress = joinAddressParts([
    customer.address_company,
    customer.street_number_company,
    customer.postcode_company,
    customer.city_company,
  ]);
  const isCompany = Boolean(customer.company_buy);

  return {
    ...EMPTY_QUOTE_CUSTOMER_DATA,
    purchaseType: isCompany ? 'company' : 'person',
    companyIco: customer.ico ?? '',
    companyName: customer.company ?? customer.company_name ?? '',
    companyDic: customer.dic ?? '',
    companyIcDph: customer.ic_dph ?? '',
    address: isCompany
      ? customer.address_company || customer.address || ''
      : customer.address || '',
    streetNumber: isCompany
      ? customer.street_number_company || customer.street_number || ''
      : customer.street_number || '',
    city: isCompany ? customer.city_company || customer.city || '' : customer.city || '',
    postcode: isCompany
      ? customer.postcode_company || customer.postcode || ''
      : customer.postcode || '',
    billingAddress: isCompany ? companyAddress || personalAddress : personalAddress,
    contactName: isCompany
      ? customerExtraString(customer, 'company_contact_name') || personalName
      : personalName,
    email: isCompany
      ? customerExtraString(customer, 'company_mail') || customer.email_company || customer.email
      : customer.email,
    phone: isCompany
      ? customerExtraString(customer, 'company_phone') || customer.phone || ''
      : customer.phone || '',
  };
}

function getCustomerDisplayName(customer: Customer | null | undefined) {
  return [customer?.name, customer?.surname].filter(Boolean).join(' ').trim();
}

function isQuoteCustomerDataTouched(customerData: QuoteCustomerData) {
  return Object.entries(customerData).some(
    ([key, value]) => key !== 'purchaseType' && typeof value === 'string' && Boolean(value.trim()),
  );
}

function getProductConfiguratorSlug(config: ConfiguratorState) {
  const selectedProduct = getSelectedOptions(config).product as ConfiguratorData['product'] & {
    configuratorSlug?: string;
    slug?: string;
  };

  return (
    selectedProduct.configuratorSlug ||
    selectedProduct.slug ||
    PRODUCT_CONFIGURATOR_SLUGS[selectedProduct.id] ||
    selectedProduct.id
  );
}

function readNestedString(source: Record<string, unknown> | null | undefined, keys: string[]) {
  if (!source) return '';

  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }

  return '';
}

function getConfigurationProductName(configuration: StoreDashboardConfiguration) {
  const customName = readNestedString(configuration.payload, [
    'configurationName',
    'configuration_name',
    'fileName',
    'file_name',
  ]);
  const payloadProduct =
    configuration.payload?.product && typeof configuration.payload.product === 'object'
      ? (configuration.payload.product as Record<string, unknown>)
      : null;
  const productName = readNestedString(payloadProduct, ['name', 'title']);

  return (
    customName ||
    resolveLocalizedValue(configuration.configurator?.name) ||
    productName ||
    'Konfigurácia'
  );
}

function getConfigurationLookupKey(configuration: StoreDashboardConfiguration | null | undefined) {
  return configuration?.token || (configuration?.id != null ? String(configuration.id) : '');
}

function getConfigurationCode(configuration: StoreDashboardConfiguration) {
  return (
    readNestedString(configuration.configuration_output, [
      'quoteId',
      'quote_id',
      'code',
      'number',
    ]) ||
    readNestedString(configuration.calculation, ['quoteId', 'quote_id', 'code', 'number']) ||
    readNestedString(configuration.payload, ['quoteId', 'quote_id', 'code', 'number']) ||
    (configuration.token ? `#${configuration.token.slice(0, 8).toUpperCase()}` : '')
  );
}

function getConfigurationStep(configuration: StoreDashboardConfiguration) {
  const stepValue = configuration.payload?.currentStep ?? configuration.payload?.step;
  const parsedStep = typeof stepValue === 'number' ? stepValue : Number(stepValue);

  return Number.isFinite(parsedStep) && parsedStep > 0 ? `Krok ${parsedStep}/4` : undefined;
}

function getConfigurationStatusValues(configuration: StoreDashboardConfiguration) {
  const topLevelStatus = (configuration.status ?? '').trim().toLowerCase();
  const payloadStatus = readNestedString(configuration.payload, [
    'status',
    'configuration_status',
    'configurationStatus',
  ])
    .trim()
    .toLowerCase();

  return { topLevelStatus, payloadStatus };
}

function isDraftConfiguration(configuration: StoreDashboardConfiguration) {
  const { topLevelStatus, payloadStatus } = getConfigurationStatusValues(configuration);

  if (topLevelStatus) return DRAFT_CONFIGURATION_STATUSES.has(topLevelStatus);
  if (SUBMITTED_CONFIGURATION_STATUSES.has(payloadStatus)) return false;

  return DRAFT_CONFIGURATION_STATUSES.has(payloadStatus);
}

function getConfigurationStatusForUpdate(configuration: StoreDashboardConfiguration) {
  const { topLevelStatus, payloadStatus } = getConfigurationStatusValues(configuration);
  return topLevelStatus || payloadStatus || STORE_CONFIGURATION_STATUS_CREATED;
}

function withDefaultConfigurationName(config: ConfiguratorState): ConfiguratorState {
  if (config.configurationName?.trim()) return config;

  return {
    ...config,
    configurationName: getSelectedOptions(config).product.name,
  };
}

function buildNamedProductPatch(productId: string, config: ConfiguratorState) {
  const patch = buildProductPatch(productId, config);
  const currentName = config.configurationName?.trim();
  const currentProductName = getSelectedOptions(config).product.name.trim();

  if (currentName && currentName !== currentProductName) return patch;

  const nextConfig = { ...config, ...patch };
  return {
    ...patch,
    configurationName: getSelectedOptions(nextConfig).product.name,
  };
}

function mapDashboardConfiguration(
  configuration: StoreDashboardConfiguration,
  sentLabel: string,
): DashboardItem {
  const productName = getConfigurationProductName(configuration);
  const code = getConfigurationCode(configuration);
  const date = formatDashboardDate(configuration.created_at ?? configuration.updated_at);
  const step = getConfigurationStep(configuration);
  const { topLevelStatus, payloadStatus } = getConfigurationStatusValues(configuration);
  const status = topLevelStatus || payloadStatus;
  const isFinished = FINISHED_CONFIGURATION_STATUSES.has(status);

  if (isDraftConfiguration(configuration)) {
    return {
      id: configuration.token ?? code ?? productName,
      title: productName,
      subtitle: [date ? `Začaté ${date}` : '', step].filter(Boolean).join(' · '),
      step,
      configuration,
    };
  }

  return {
    id: configuration.token ?? code ?? productName,
    title: code ? `${code} · ${productName}` : productName,
    subtitle: date ? `${sentLabel} ${date}` : sentLabel,
    status: isFinished ? 'Vybavená' : 'Čaká',
    statusTone: isFinished ? 'success' : 'neutral',
    configuration,
  };
}

function buildDashboardState(
  configurations: StoreDashboardConfiguration[],
  sentLabel: string,
): DashboardState {
  const mapped = configurations.map(configuration =>
    mapDashboardConfiguration(configuration, sentLabel),
  );

  return {
    drafts: mapped.filter((_, index) => isDraftConfiguration(configurations[index])),
    quotes: mapped.filter((_, index) => !isDraftConfiguration(configurations[index])),
    isLoading: false,
    error: null,
  };
}

async function loadDashboardState(
  authToken: string | null | undefined,
  locale: string | undefined,
  sentLabel: string,
): Promise<DashboardState> {
  const response = await fetch(getDashboardApiUrl(), {
    credentials: 'include',
    headers: getStoreHeaders(authToken, locale),
  });

  if (!response.ok) {
    throw new Error(`Dashboard configurations request failed with ${response.status}`);
  }

  const payload = (await response.json()) as { data?: StoreDashboardConfiguration[] };
  const configurations = Array.isArray(payload.data) ? payload.data : [];
  return buildDashboardState(configurations, sentLabel);
}

async function createStoreConfiguration({
  authToken,
  config,
  customerData,
  locale,
  status = STORE_CONFIGURATION_STATUS_CREATED,
  currentStep = 4,
}: {
  authToken?: string | null;
  config: ConfiguratorState;
  customerData: QuoteCustomerData;
  locale?: string;
  status?: StoreCreatableConfigurationStatus;
  currentStep?: number;
}) {
  const slug = getProductConfiguratorSlug(config);
  const response = await fetch(
    getStoreApiUrl(`/store/product-configurators/${encodeURIComponent(slug)}/configurations`),
    {
      method: 'POST',
      credentials: 'include',
      headers: getStoreHeaders(authToken, locale, true),
      body: JSON.stringify(await prepareCaptchaBody({
        payload: {
          ...config,
          currentStep,
          status,
        },
        status,
        customer_data: serializeQuoteCustomerData(customerData),
      }, CaptchaAction.CONFIGURATOR_SUBMIT)),
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Configuration request failed with ${response.status}`);
  }

  const payload = (await response.json()) as { data?: StoredConfiguratorConfiguration };
  return payload.data ?? null;
}

async function loadStoreConfigurationDetail({
  authToken,
  configurationKey,
  locale,
}: {
  authToken?: string | null;
  configurationKey: string;
  locale?: string;
}) {
  const response = await fetch(
    getStoreApiUrl(
      `/store/product-configurators/configurations/${encodeURIComponent(configurationKey)}`,
    ),
    {
      credentials: 'include',
      headers: getStoreHeaders(authToken, locale),
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Configuration detail request failed with ${response.status}`);
  }

  const payload = (await response.json()) as { data?: StoredConfiguratorConfiguration };
  return payload.data ?? null;
}

async function updateStoreConfiguration({
  authToken,
  configuration,
  config,
  customerData,
  locale,
  status,
  currentStep = 4,
}: {
  authToken?: string | null;
  configuration: StoreDashboardConfiguration;
  config: ConfiguratorState;
  customerData: QuoteCustomerData;
  locale?: string;
  status?: string;
  currentStep?: number;
}) {
  const configurationKey = getConfigurationLookupKey(configuration);
  if (!configurationKey) {
    throw new Error('Stored configuration does not have an editable token.');
  }
  const resolvedStatus = status ?? getConfigurationStatusForUpdate(configuration);

  const response = await fetch(
    getStoreApiUrl(
      `/store/product-configurators/configurations/${encodeURIComponent(configurationKey)}`,
    ),
    {
      method: 'PUT',
      credentials: 'include',
      headers: getStoreHeaders(authToken, locale, true),
      body: JSON.stringify({
        payload: {
          ...config,
          currentStep,
          status: resolvedStatus,
        },
        status: resolvedStatus,
        customer_data: serializeQuoteCustomerData(customerData),
      }),
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Configuration update failed with ${response.status}`);
  }

  const payload = (await response.json()) as { data?: StoredConfiguratorConfiguration };
  return payload.data ?? null;
}

function normalizeStoredConfiguratorState(
  configuration: StoreDashboardConfiguration | null | undefined,
) {
  const payload = asRecord(configuration?.payload);
  if (!payload) return null;

  const productTypeId = readRecordString(payload, ['productTypeId', 'product_type_id']);
  if (!productTypeId) return null;
  const configurationName = readRecordString(payload, [
    'configurationName',
    'configuration_name',
    'fileName',
    'file_name',
  ]);

  const productConfig = getConfiguratorDataForProduct(productTypeId);
  return withDefaultConfigurationName({
    ...(productConfig.defaults as ConfiguratorState),
    ...payload,
    ...(configurationName ? { configurationName } : {}),
  } as ConfiguratorState);
}

function getInitialConfigurationQueryKey() {
  if (typeof window === 'undefined') return '';

  const params = new URLSearchParams(window.location.search);
  return (
    params.get('config') ||
    params.get('configuration') ||
    params.get('configurationToken') ||
    params.get('token') ||
    ''
  ).trim();
}

function setConfigurationQueryKey(configurationKey?: string) {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  if (configurationKey) {
    url.searchParams.set('config', configurationKey);
  } else {
    url.searchParams.delete('config');
    url.searchParams.delete('configuration');
    url.searchParams.delete('configurationToken');
    url.searchParams.delete('token');
  }

  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}
const SUCCESS_CONTENT = {
  configurationCode: '#LX-2026-0142',
  sentAt: '15. máj 2026 · 14:32',
  title: 'text4086',
  subtitle: 'Ďakujeme za vašu konfiguráciu. Náš tím ju skontroluje a ozve sa vám.',
  nextSteps: [
    {
      title: 'Skontrolujeme vašu konfiguráciu',
      description: 'Prejdeme všetky parametre, vyrobiteľnosť a pripravené výrobné podklady.',
    },
    {
      title: 'Pripravíme výrobné výstupy',
      description: 'Výkres a DXF výstup zostanú dostupné v detaile konfigurácie.',
    },
    {
      title: 'Potvrdíte objednávku',
      description: 'Po vašom súhlase spustíme výrobu a dohodneme dodanie.',
    },
  ],
} as const;
const HOLE_SHAPE_OPTIONS = [
  { id: 'round', name: 'Kruhový otvor' },
  { id: 'slot', name: 'Oválny otvor' },
  { id: 'rect', name: 'Obdĺžnik / štvorec' },
];
const BEND_ORIENTATION_OPTIONS = [
  { id: 'horizontal', name: 'Vodorovná línia' },
  { id: 'vertical', name: 'Zvislá línia' },
];
const BEND_DIRECTION_OPTIONS = [
  { id: 'up', name: 'Ohyb nahor' },
  { id: 'down', name: 'Ohyb nadol' },
];
const BEND_POSITION_MODE_OPTIONS = [
  { id: 'manual', name: 'Rucne' },
  { id: 'partCenter', name: 'Stred dielu' },
  { id: 'featureCenter', name: 'Stred vyrezu' },
  { id: 'betweenFeatures', name: 'Medzi vyrezmi' },
];
const OUTLINE_NOTCH_EDGE_OPTIONS: Array<{
  id: OutlineNotchEdge;
  label: string;
  marker: string;
}> = [
  { id: 'top', label: 'Horná hrana', marker: 'border-t-4' },
  { id: 'right', label: 'Pravá hrana', marker: 'border-r-4' },
  { id: 'bottom', label: 'Dolná hrana', marker: 'border-b-4' },
  { id: 'left', label: 'Ľavá hrana', marker: 'border-l-4' },
];
const CORNER_TYPE_OPTIONS = [
  { id: 'chamfer-irregular', name: 'Nepravidelné seknutie' },
  { id: 'square', name: 'Bez úpravy' },
  { id: 'chamfer', name: 'Seknutý' },
  { id: 'radius', name: 'Zaoblený' },
];
const CORNER_FIELDS = [
  { key: 'TopLeft', label: 'Ľavý horný roh' },
  { key: 'TopRight', label: 'Pravý horný roh' },
  { key: 'BottomRight', label: 'Pravý dolný roh' },
  { key: 'BottomLeft', label: 'Ľavý dolný roh' },
] as const;
type CornerFieldKey = (typeof CORNER_FIELDS)[number]['key'];
type CornerEditType = 'square' | 'chamfer' | 'chamfer-irregular' | 'radius';

function isHorizontalOutlineEdge(edge?: OutlineNotchEdge) {
  return edge === 'bottom' || edge === 'top' || !edge;
}

function isLengthBasedHoleShape(shape?: string | null) {
  return shape === 'slot' || shape === 'rect';
}

function holePrimaryDimensionLabel(shape?: string | null) {
  return isLengthBasedHoleShape(shape) ? 'Šírka' : 'Priemer';
}

function holeSecondaryDimensionLabel(shape?: string | null) {
  return shape === 'rect' ? 'Dĺžka obdĺžnika' : 'Dĺžka oválu';
}

function holeTypeName(shape: HoleShape) {
  if (shape === 'slot') return 'Ovál';
  if (shape === 'rect') return 'Obdĺžnik';
  return 'Kruh';
}

function formatCompactNumber(value: number, maxFractionDigits = 3) {
  return new Intl.NumberFormat('sk-SK', {
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
}

function formatLeadTime(days: number) {
  const roundedDays = Math.max(0, Math.round(days));
  const unit = roundedDays === 1 ? 'deň' : roundedDays >= 2 && roundedDays <= 4 ? 'dni' : 'dní';

  return `~ ${formatCompactNumber(roundedDays, 0)} ${unit}`;
}

function cornerTypeKey(corner: CornerFieldKey) {
  return `corner${corner}Type` as keyof ConfiguratorState;
}

function cornerSizeKey(corner: CornerFieldKey) {
  return `corner${corner}Mm` as keyof ConfiguratorState;
}

function cornerChamferXKey(corner: CornerFieldKey) {
  return `corner${corner}ChamferXMm` as keyof ConfiguratorState;
}

function cornerChamferYKey(corner: CornerFieldKey) {
  return `corner${corner}ChamferYMm` as keyof ConfiguratorState;
}

function getConfigNumber(config: ConfiguratorState, key: keyof ConfiguratorState, fallback = 0) {
  const value = Number(config[key] ?? fallback);
  return Number.isFinite(value) ? value : fallback;
}

function getCornerEditType(config: ConfiguratorState, corner: CornerFieldKey): CornerEditType {
  const value = config[cornerTypeKey(corner)];
  return value === 'chamfer' || value === 'chamfer-irregular' || value === 'radius'
    ? value
    : 'square';
}

function getCornerChamferValues(config: ConfiguratorState, corner: CornerFieldKey) {
  const fallback = getConfigNumber(config, cornerSizeKey(corner));
  return {
    xMm: getConfigNumber(config, cornerChamferXKey(corner), fallback),
    yMm: getConfigNumber(config, cornerChamferYKey(corner), fallback),
  };
}

function getCornerSummaryLine(config: ConfiguratorState, corner: CornerFieldKey) {
  const type = getCornerEditType(config, corner);
  const size = getConfigNumber(config, cornerSizeKey(corner));
  const chamfer = getCornerChamferValues(config, corner);

  if (type === 'square') return 'bez úpravy';
  if (type === 'radius') return `zaoblený R${formatNumber(size, 0)} mm`;
  if (type === 'chamfer-irregular') {
    return `seknutý ${formatNumber(chamfer.xMm, 0)} × ${formatNumber(chamfer.yMm, 0)} mm`;
  }

  return `seknutý ${formatNumber(size, 0)} mm`;
}

function getCornerSummary(config: ConfiguratorState) {
  return CORNER_FIELDS.map(
    corner => `${corner.label}: ${getCornerSummaryLine(config, corner.key)}`,
  ).join(' · ');
}

function toEditableOutlineNotches(config: ConfiguratorState): OutlineNotchOverride[] {
  return getProductOutlineNotches(config).map(notch => ({
    id: notch.id,
    label: notch.label,
    edge: notch.edge,
    centerMm: roundGeometryMm(notch.centerMm),
    widthMm: roundGeometryMm(notch.widthMm),
    depthMm: roundGeometryMm(notch.depthMm),
    radiusMm: roundGeometryMm(notch.radiusMm),
    mouthRadiusMm: roundGeometryMm(notch.mouthRadiusMm),
  }));
}

function toEditableInternalCuts(config: ConfiguratorState): InternalCutOverride[] {
  return getProductInternalCuts(config).map(cut => ({
    id: cut.id,
    label: cut.label,
    shape: cut.shape,
    xMm: roundGeometryMm(cut.xMm),
    yMm: roundGeometryMm(cut.yMm),
    widthMm: roundGeometryMm(cut.widthMm),
    lengthMm: roundGeometryMm(cut.lengthMm),
    orientation: cut.orientation,
    angleDeg: roundGeometryMm(cut.angleDeg),
    cornerRadiusMm: roundGeometryMm(cut.cornerRadiusMm),
  }));
}

function getInternalCutsSummary(config: ConfiguratorState) {
  const cuts = getProductInternalCuts(config);
  if (!cuts.length) return 'Bez vnútorných rezov';

  return cuts
    .map(cut =>
      cut.shape === 'round'
        ? `${cut.label}: Ø ${formatNumber(cut.widthMm, 0)} mm`
        : `${cut.label}: ${formatNumber(cut.widthMm, 0)} × ${formatNumber(cut.lengthMm, 0)} mm`,
    )
    .join(' · ');
}

function toEditableBendLines(config: ConfiguratorState): BendLineOverride[] {
  return getProductBendLines(config).map(line => ({
    id: line.id,
    label: line.label,
    orientation: line.orientation,
    positionMm: roundGeometryMm(line.positionMm),
    positionMode: line.positionMode,
    constraint: line.constraint,
    angleDeg: line.angleDeg,
    direction: line.direction,
  }));
}

function getBendLinesSummary(config: ConfiguratorState) {
  const bendLines = getProductBendLines(config);
  if (!bendLines.length) return 'Bez ohybových línií';

  return bendLines
    .map(
      line =>
        `${line.label}: ${line.orientation === 'horizontal' ? 'Y' : 'X'} ${formatNumber(
          line.positionMm,
          0,
        )} mm · ${line.angleDeg}° ${line.direction === 'up' ? 'nahor' : 'nadol'}`,
    )
    .join(' · ');
}

function DashboardConfigurationPreview({
  configuration,
}: {
  configuration: StoreDashboardConfiguration;
}) {
  const previewConfig = normalizeStoredConfiguratorState(configuration);

  return (
    <div className="h-12 w-12 overflow-hidden border border-white/10 bg-white">
      {previewConfig ? (
        <ProductTypePreview3D config={previewConfig} variant="card" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[#f5f5f5] px-1 text-center text-[9px] font-semibold leading-tight text-[#5f6972]">
          Bez náhľadu
        </div>
      )}
    </div>
  );
}

const PRODUCT_PREVIEW_PATCHES: Record<string, Partial<ConfiguratorState>> = {
  'lisonex-bent-slot-link': {
    widthMm: 61.142,
    lengthMm: 83.142,
    thicknessMm: 2,
  },
  'lisonex-base-blank-plate': {
    widthMm: 1000,
    lengthMm: 1000,
    thicknessMm: 50,
  },
};

function buildProductPreviewConfig(productId: string): ConfiguratorState {
  const previewData = getConfiguratorDataForProduct(productId);
  const previewConfig = buildProductPatch(productId, previewData.defaults as ConfiguratorState);
  const previewPatch = PRODUCT_PREVIEW_PATCHES[productId] ?? {};
  if (productId === 'lisonex-base-blank-plate') {
    return {
      ...previewConfig,
      ...previewPatch,
      finishId: 'black',
      holeCount: 0,
      holePatternId: 'none',
      holePositions: [],
      holeDiameters: [],
      holeShapes: [],
      holeSlotLengths: [],
      holeSlotOrientations: [],
      holeSlotAnglesDeg: [],
      internalCuts: [],
      outlineNotches: [],
      bendLines: [],
      outlineOuterRadiusMm: 0,
      outlineCenterGapMm: 0,
      outlineTopNotchDepthMm: 0,
      edgeRadiusMm: 0,
    };
  }

  const previewHoleDiameterMm = Math.max(previewConfig.holeDiameterMm, 30);
  const holeDiameterMm = previewPatch.holeDiameterMm ?? previewHoleDiameterMm;

  return {
    ...previewConfig,
    ...previewPatch,
    finishId: 'black',
    holeDiameterMm,
  };
}

function Panel({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }) {
  return (
    <div
      className={cn('bg-[#15354d] text-white shadow-[0_24px_70px_rgba(0,0,0,0.22)]', className)}
      {...props}
    >
      {children}
    </div>
  );
}

const sectionActionButtonClass =
  'grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 bg-[#092031] text-white transition hover:border-[#e87500] hover:text-[#e87500]';

function SectionPanelHeader({
  title,
  onBack,
  onAdd,
  addLabel = 'Pridať',
}: {
  title: string;
  onBack: () => void;
  onAdd?: () => void;
  addLabel?: string;
}) {
  const { t } = useTranslations();

  return (
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-xl font-semibold tracking-normal text-white lg:text-2xl">{title}</h3>
      <div className="flex items-center gap-2">
        {onAdd && (
          <button
            type="button"
            className="grid h-9 w-9 place-items-center rounded-full bg-black text-white transition hover:bg-[#e87500]"
            onClick={onAdd}
            aria-label={addLabel}
            title={addLabel}
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          className={sectionActionButtonClass}
          onClick={onBack}
          aria-label={t('text4084')}
          title={t('text4084')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

type SectionIconProps = React.SVGProps<SVGSVGElement>;

function CutsSectionIcon({ className, ...props }: SectionIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <rect x="4.75" y="5.25" width="14.5" height="13.5" rx="1.8" strokeWidth="1.75" />
      <path d="M12 3.75v16.5" strokeWidth="1.35" strokeDasharray="2 2.4" />
      <rect
        x="7.35"
        y="10"
        width="9.3"
        height="4"
        rx="2"
        fill="currentColor"
        opacity="0.14"
        strokeWidth="0"
      />
      <path d="M7.35 12h9.3" strokeWidth="1.85" />
    </svg>
  );
}

function BendsSectionIcon({ className, ...props }: SectionIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M4.75 16.75h7.4l6.95-6.4v5.05l-5.1 4.85H4.75z"
        fill="currentColor"
        opacity="0.12"
        strokeWidth="0"
      />
      <path d="M4.75 16.75h7.4l6.95-6.4" strokeWidth="1.95" />
      <path d="M12.15 16.75V5.25" strokeWidth="1.35" strokeDasharray="2 2.4" />
      <path d="M19.1 10.35v5.05l-5.1 4.85" strokeWidth="1.55" opacity="0.9" />
      <path d="M16.45 8.85h3.35v3.35" strokeWidth="1.35" opacity="0.7" />
    </svg>
  );
}

function CornersSectionIcon({ className, ...props }: SectionIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M5.25 18.75V9.25a4 4 0 0 1 4-4h6.65l2.85 2.85v10.65z"
        fill="currentColor"
        opacity="0.12"
        strokeWidth="0"
      />
      <path d="M5.25 18.75V9.25a4 4 0 0 1 4-4h6.65l2.85 2.85v10.65z" strokeWidth="1.75" />
      <path d="M15.9 5.25v2.85h2.85" strokeWidth="1.35" />
      <path d="M5.25 14.9c2.25-.65 3.35-1.95 3.55-4.05" strokeWidth="1.25" opacity="0.68" />
      <path d="M5.25 18.75h13.5" strokeWidth="1.25" opacity="0.45" />
    </svg>
  );
}

function LisonexLogo() {
  return (
    <div className="text-center leading-none">
      <div className="text-[34px] font-extrabold tracking-[-0.04em] text-white sm:text-[42px]">
        LISONEX
      </div>
      <div className="mt-1 text-[12px] font-semibold uppercase tracking-[0.48em] text-[#e87500] sm:text-[15px]">
        Konfigurátor
      </div>
    </div>
  );
}

function LisonexTopBar({
  leftLabel,
  onLeftClick,
  onLogout,
  userName,
}: {
  leftLabel?: string;
  onLeftClick?: () => void;
  onLogout: () => void;
  userName?: string;
}) {
  return (
    <header className="sticky top-0 z-[80] shrink-0 bg-[#15354d] px-5 py-5 text-white lg:px-16">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        {leftLabel && onLeftClick ? (
          <button
            type="button"
            onClick={onLeftClick}
            className="flex w-fit items-center gap-3 text-sm font-semibold text-white/90 transition hover:text-white sm:text-base"
          >
            <X className="h-5 w-5" />
            <span>{leftLabel}</span>
          </button>
        ) : (
          <div />
        )}
        <LisonexLogo />
        <div className="flex items-center justify-end gap-3">
          {userName ? (
            <span className="hidden max-w-[220px] truncate text-sm font-semibold text-white/75 sm:block">
              {userName}
            </span>
          ) : null}
          <button
            type="button"
            onClick={onLogout}
            className="w-fit border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 transition hover:border-white/35 hover:bg-white/15 hover:text-white sm:px-5 sm:text-base"
          >
            Odhlásiť sa
          </button>
        </div>
      </div>
    </header>
  );
}

export function LisonexConfiguratorStyles() {
  return (
    <style jsx global>{`
      body:has([data-configurator-main]) .ea-header,
      body:has([data-configurator-main]) footer,
      body.ea-configurator-active .ea-header,
      body.ea-configurator-active footer {
        display: none !important;
      }

      body.ea-configurator-active {
        background: #071d2b;
        overflow: hidden;
      }

      body.ea-configurator-flow-active {
        overflow: hidden;
      }

      .lisonex-configurator {
        --lx-bg: #071d2b;
        --lx-panel: #15354d;
        --lx-panel-dark: #092031;
        --lx-line: rgba(255, 255, 255, 0.18);
        --lx-muted: rgba(255, 255, 255, 0.58);
        --lx-accent: #e87500;
        --lx-flow-content-height: calc(100svh - 100px - 81px - 56px);
        display: flex;
        height: 100dvh;
        min-height: 0;
        flex-direction: column;
        overflow: hidden;
      }

      .lisonex-configurator[data-configurator-flow-active='true'] {
        overflow: hidden;
      }

      .lisonex-configurator [data-configurator-dashboard] {
        min-height: 0;
      }

      .lisonex-configurator [data-configurator-dashboard-grid] {
        gap: clamp(18px, 2.35vh, 24px);
      }

      .lisonex-configurator [data-configurator-dashboard-lists] {
        gap: clamp(18px, 2.8vh, 32px);
      }

      .lisonex-configurator [data-configurator-dashboard-list-scroll] {
        scrollbar-width: thin;
        scrollbar-color: rgba(232, 117, 0, 0.7) rgba(255, 255, 255, 0.08);
      }

      .lisonex-configurator [data-configurator-dashboard-list-scroll]::-webkit-scrollbar {
        width: 5px;
      }

      .lisonex-configurator [data-configurator-dashboard-list-scroll]::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.08);
      }

      .lisonex-configurator [data-configurator-dashboard-list-scroll]::-webkit-scrollbar-thumb {
        background: rgba(232, 117, 0, 0.75);
      }

      .lisonex-configurator [data-configurator-stepper] {
        position: static !important;
        border: 0 !important;
        background: transparent !important;
        color: #fff !important;
        backdrop-filter: none !important;
      }

      .lisonex-configurator [data-configurator-stepper] + div[aria-hidden='true'] {
        display: none !important;
      }

      .lisonex-configurator [data-configurator-stepper] > div {
        max-width: 1660px !important;
        padding: 30px 32px 0 !important;
      }

      .lisonex-configurator [data-configurator-stepper] button {
        border-radius: 0 !important;
      }

      .lisonex-configurator [data-configurator-step-shell] h1,
      .lisonex-configurator [data-configurator-step-shell] h2,
      .lisonex-configurator [data-configurator-step-shell] h3 {
        color: #fff !important;
      }

      .lisonex-configurator [data-configurator-step-shell] p,
      .lisonex-configurator dt,
      .lisonex-configurator [data-configurator-sidebar] p,
      .lisonex-configurator [data-configurator-sidebar] span,
      .lisonex-configurator [data-configurator-preview-panel] p {
        color: var(--lx-muted);
      }

      .lisonex-configurator input,
      .lisonex-configurator select {
        background: var(--lx-panel-dark) !important;
        color: #fff !important;
        border-color: rgba(255, 255, 255, 0.14) !important;
      }

      .lisonex-configurator input::placeholder {
        color: rgba(255, 255, 255, 0.45) !important;
      }

      .lisonex-configurator input[type='range'] {
        accent-color: var(--lx-accent) !important;
        background: transparent !important;
      }

      .lisonex-configurator
        [data-configurator-date-input][data-empty='true']::-webkit-date-and-time-value,
      .lisonex-configurator
        [data-configurator-date-input][data-empty='true']::-webkit-datetime-edit,
      .lisonex-configurator
        [data-configurator-date-input][data-empty='true']::-webkit-datetime-edit-day-field,
      .lisonex-configurator
        [data-configurator-date-input][data-empty='true']::-webkit-datetime-edit-month-field,
      .lisonex-configurator
        [data-configurator-date-input][data-empty='true']::-webkit-datetime-edit-year-field,
      .lisonex-configurator
        [data-configurator-date-input][data-empty='true']::-webkit-datetime-edit-text {
        color: transparent !important;
      }

      .lisonex-configurator [data-configurator-preview-panel] {
        border-radius: 0 !important;
      }

      .lisonex-configurator [data-configurator-sidebar] > div {
        border-radius: 0 !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#fafafa\\],
      .lisonex-configurator [data-configurator-sidebar] .bg-white,
      .lisonex-configurator [data-configurator-summary] .bg-\\[\\#f2f2f2\\],
      .lisonex-configurator [data-configurator-summary] .bg-white {
        background: var(--lx-panel-dark) !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .border-\\[\\#eeeeee\\],
      .lisonex-configurator [data-configurator-sidebar] .border-\\[\\#d9d9d9\\],
      .lisonex-configurator [data-configurator-summary] .border-\\[\\#d9d9d9\\] {
        border-color: rgba(255, 255, 255, 0.14) !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .text-\\[\\#777\\],
      .lisonex-configurator [data-configurator-summary] .text-\\[\\#777\\],
      .lisonex-configurator [data-configurator-success] .text-\\[\\#777\\] {
        color: var(--lx-muted) !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .text-\\[\\#e63208\\],
      .lisonex-configurator [data-configurator-summary] .text-\\[\\#e63208\\],
      .lisonex-configurator [data-configurator-success] .text-\\[\\#e63208\\] {
        color: var(--lx-accent) !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\],
      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] p,
      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] span,
      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] strong,
      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] label {
        color: #082033 !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] .text-\\[\\#777\\] {
        color: #526a7a !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] .text-\\[\\#e63208\\],
      .lisonex-configurator
        [data-configurator-sidebar]
        .bg-\\[\\#f5fff8\\]
        button.text-\\[\\#e63208\\] {
        color: #e63208 !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] .bg-black,
      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] .bg-black * {
        color: #fff !important;
      }

      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] input,
      .lisonex-configurator [data-configurator-sidebar] .bg-\\[\\#f5fff8\\] select {
        background: #092031 !important;
        color: #fff !important;
      }

      .lisonex-configurator [data-configurator-bottom-actions] {
        border-color: rgba(255, 255, 255, 0.12) !important;
        background: #050505 !important;
      }

      .lisonex-configurator [data-configurator-bottom-actions] > div {
        max-width: 1660px !important;
      }

      .lisonex-configurator [data-configurator-success] {
        flex: 1 1 auto;
        min-height: 0;
        overflow-x: hidden;
        overflow-y: auto;
      }

      .lisonex-configurator [data-configurator-flow-shell] {
        height: calc(100svh - 100px - 81px);
        min-height: 0;
        overflow: hidden;
        padding-bottom: 24px !important;
        padding-top: 32px !important;
      }

      .lisonex-configurator [data-configurator-flow-content='steps'] {
        display: flex !important;
        align-items: stretch !important;
        height: 100% !important;
        min-height: 0 !important;
        overflow-x: hidden !important;
        overflow-y: auto !important;
      }

      .lisonex-configurator
        [data-configurator-flow-content='steps']
        > [data-configurator-step-shell] {
        flex: 1 1 auto !important;
        width: 100% !important;
        min-width: 0 !important;
      }

      .lisonex-configurator [data-configurator-flow-content='parameters'] {
        display: flex !important;
        width: 100% !important;
        align-items: stretch !important;
        height: 100% !important;
        min-height: 0 !important;
        overflow-x: hidden !important;
        overflow-y: auto !important;
      }

      .lisonex-configurator
        [data-configurator-flow-content='parameters']
        > [data-configurator-step-shell] {
        flex: 1 1 auto !important;
        width: 100% !important;
        max-width: none !important;
        min-width: 0 !important;
      }

      .lisonex-configurator [data-configurator-stepper] {
        height: 100%;
        min-height: 0 !important;
        overflow: hidden;
      }

      .lisonex-configurator [data-configurator-stepper-steps] {
        scrollbar-width: thin;
        scrollbar-color: rgba(232, 117, 0, 0.7) rgba(255, 255, 255, 0.08);
      }

      .lisonex-configurator [data-configurator-stepper-steps]::-webkit-scrollbar {
        width: 5px;
      }

      .lisonex-configurator [data-configurator-stepper-steps]::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.08);
      }

      .lisonex-configurator [data-configurator-stepper-steps]::-webkit-scrollbar-thumb {
        background: rgba(232, 117, 0, 0.75);
      }

      .lisonex-configurator [data-configurator-parameters-layout] {
        display: grid !important;
        grid-template-columns: minmax(0, 1fr) !important;
        height: auto !important;
        min-height: 0 !important;
        overflow: visible !important;
      }

      .lisonex-configurator [data-configurator-preview-sticky] {
        order: 2;
        position: static !important;
        top: auto !important;
        height: auto !important;
        min-height: 0 !important;
      }

      .lisonex-configurator [data-configurator-preview-panel] {
        height: auto !important;
        min-height: 0 !important;
      }

      .lisonex-configurator [data-configurator-metrics] {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        row-gap: 12px;
      }

      .lisonex-configurator [data-configurator-metric] {
        min-width: 0;
      }

      .lisonex-configurator [data-configurator-metric] p,
      .lisonex-configurator [data-configurator-metric] strong {
        white-space: nowrap;
      }

      .lisonex-configurator [data-configurator-sidebar] {
        order: 1;
        height: auto !important;
        min-height: 0 !important;
        overflow: visible !important;
      }

      .lisonex-configurator [data-configurator-mobile-section-nav] {
        display: none !important;
      }

      .lisonex-configurator [data-configurator-sidebar-shortcut] {
        border-color: rgba(255, 255, 255, 0.14) !important;
        background: #092031 !important;
        color: #fff !important;
      }

      .lisonex-configurator [data-configurator-sidebar-shortcut]:hover {
        border-color: rgba(232, 117, 0, 0.75) !important;
        background: #0b2a42 !important;
      }

      .lisonex-configurator [data-configurator-sidebar-shortcut][data-active='true'],
      .lisonex-configurator [data-configurator-sidebar-shortcut][data-active='true']:hover {
        border-color: #e87500 !important;
        background: #e87500 !important;
        color: #fff !important;
      }

      .lisonex-configurator [data-configurator-sidebar-shortcut] strong {
        color: #fff !important;
      }

      .lisonex-configurator [data-configurator-sidebar-shortcut] span {
        color: rgba(255, 255, 255, 0.62) !important;
      }

      @media (max-width: 1023px) {
        .lisonex-configurator {
          --lx-flow-content-height: calc(100svh - 86px - 116px);
        }

        .lisonex-configurator > header {
          padding: 12px 14px !important;
        }

        .lisonex-configurator > header > div {
          grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) !important;
          gap: 8px !important;
        }

        .lisonex-configurator > header button {
          gap: 7px !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          font-size: 12px !important;
        }

        .lisonex-configurator > header button:last-child {
          padding: 8px 10px !important;
        }

        .lisonex-configurator > header .text-\\[34px\\] {
          font-size: 26px !important;
        }

        .lisonex-configurator > header .text-\\[12px\\] {
          font-size: 9px !important;
          letter-spacing: 0.34em !important;
        }

        .lisonex-configurator [data-configurator-flow-shell] {
          display: flex !important;
          height: var(--lx-flow-content-height) !important;
          min-height: 0 !important;
          flex-direction: column !important;
          gap: 10px !important;
          overflow: hidden !important;
          padding: 10px 12px !important;
        }

        .lisonex-configurator [data-configurator-stepper] {
          flex: 0 0 auto !important;
          height: auto !important;
          min-height: 0 !important;
          overflow: visible !important;
          padding: 0 !important;
        }

        .lisonex-configurator [data-configurator-stepper] > div {
          max-width: none !important;
          padding: 0 !important;
        }

        .lisonex-configurator [data-configurator-stepper] > div:first-child {
          order: 2 !important;
        }

        .lisonex-configurator [data-configurator-stepper] h1 {
          font-size: 20px !important;
          line-height: 1.08 !important;
        }

        .lisonex-configurator [data-configurator-stepper] p {
          display: none !important;
        }

        .lisonex-configurator [data-configurator-stepper-steps] {
          order: 1 !important;
          margin-bottom: 8px !important;
          margin-top: 0 !important;
          flex: 0 0 auto !important;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          padding-bottom: 3px !important;
          padding-right: 0 !important;
        }

        .lisonex-configurator [data-configurator-stepper-steps] > div {
          display: flex !important;
          width: 100% !important;
          min-width: 0 !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
          flex-wrap: nowrap !important;
        }

        .lisonex-configurator [data-configurator-step-item] {
          display: flex !important;
          flex: 0 0 36px !important;
          width: 36px !important;
          height: 36px !important;
          min-width: 0 !important;
          align-items: center !important;
          justify-content: center !important;
          overflow: hidden !important;
        }

        .lisonex-configurator [data-configurator-step-item] > button {
          height: 36px !important;
          width: 36px !important;
          padding: 0 !important;
        }

        .lisonex-configurator [data-configurator-step-item] > span,
        .lisonex-configurator [data-configurator-step-label-wrap] {
          display: none !important;
        }

        .lisonex-configurator [data-configurator-step-item][data-current='true'] {
          flex: 1 1 150px !important;
          width: auto !important;
          max-width: 210px !important;
          justify-content: flex-start !important;
          background: #fff !important;
        }

        .lisonex-configurator [data-configurator-step-item][data-current='true'] > button {
          flex: 0 0 36px !important;
          background: transparent !important;
          color: #071d2b !important;
        }

        .lisonex-configurator
          [data-configurator-step-item][data-current='true']
          [data-configurator-step-label-wrap] {
          display: flex !important;
          min-width: 0 !important;
          flex: 1 1 auto !important;
          align-items: center !important;
          padding: 0 10px 0 0 !important;
        }

        .lisonex-configurator
          [data-configurator-step-item][data-current='true']
          [data-configurator-step-label] {
          display: block !important;
          min-width: 0 !important;
          overflow: hidden !important;
          padding-top: 0 !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          color: #071d2b !important;
          font-size: 12px !important;
          line-height: 1.1 !important;
        }

        .lisonex-configurator [data-configurator-flow-content] {
          min-height: 0 !important;
          flex: 1 1 auto !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          padding-right: 2px !important;
          scrollbar-width: thin;
          scrollbar-color: rgba(232, 117, 0, 0.75) rgba(255, 255, 255, 0.08);
        }

        .lisonex-configurator [data-configurator-flow-content='steps'] {
          display: block !important;
          height: auto !important;
          max-height: 100% !important;
        }

        .lisonex-configurator [data-configurator-step-shell] {
          display: flex !important;
          width: 100% !important;
          min-height: 0 !important;
          flex-direction: column !important;
          padding-bottom: 10px !important;
        }

        .lisonex-configurator [data-configurator-step-shell] > div:first-child {
          display: none !important;
        }

        .lisonex-configurator [data-configurator-product-step] {
          align-content: start !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 12px !important;
          padding-bottom: 10px !important;
          touch-action: pan-y;
        }

        .lisonex-configurator [data-configurator-product-card] {
          min-height: 226px !important;
          touch-action: pan-y;
        }

        .lisonex-configurator [data-configurator-product-card-media] {
          height: clamp(94px, 28svh, 142px) !important;
          margin-bottom: 0 !important;
        }

        .lisonex-configurator [data-configurator-product-card] > span {
          padding: 12px !important;
        }

        .lisonex-configurator [data-configurator-product-card] > span > span:first-child {
          font-size: 14px !important;
          line-height: 1.16 !important;
        }

        .lisonex-configurator [data-configurator-product-card] > span > span:nth-child(2) {
          display: -webkit-box !important;
          min-height: 0 !important;
          overflow: hidden !important;
          -webkit-box-orient: vertical !important;
          -webkit-line-clamp: 2 !important;
          font-size: 11px !important;
          line-height: 1.28 !important;
        }

        .lisonex-configurator [data-configurator-product-card] > span > span:last-child {
          margin-top: 12px !important;
          height: 36px !important;
          gap: 8px !important;
          padding-inline: 12px !important;
          font-size: 12px !important;
        }

        .lisonex-configurator [data-configurator-machine-step] {
          align-content: start !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 12px !important;
          padding-bottom: 10px !important;
          touch-action: pan-y;
        }

        .lisonex-configurator [data-configurator-machine-card] {
          display: flex !important;
          min-height: 226px !important;
          flex-direction: column !important;
          touch-action: pan-y;
        }

        .lisonex-configurator [data-configurator-machine-card-media] {
          height: clamp(94px, 28svh, 142px) !important;
          aspect-ratio: auto !important;
        }

        .lisonex-configurator [data-configurator-machine-card] > div {
          display: flex !important;
          flex: 1 1 auto !important;
          flex-direction: column !important;
          padding: 12px !important;
        }

        .lisonex-configurator [data-configurator-machine-card] h3 {
          font-size: 14px !important;
          line-height: 1.16 !important;
        }

        .lisonex-configurator [data-configurator-machine-card] p {
          display: -webkit-box !important;
          min-height: 0 !important;
          overflow: hidden !important;
          -webkit-box-orient: vertical !important;
          -webkit-line-clamp: 2 !important;
          font-size: 11px !important;
          line-height: 1.28 !important;
        }

        .lisonex-configurator [data-configurator-machine-card] > div > span {
          margin-top: 12px !important;
          height: 36px !important;
          gap: 8px !important;
          padding-inline: 12px !important;
          font-size: 12px !important;
        }

        .lisonex-configurator [data-configurator-parameters-layout] {
          display: flex !important;
          width: 100% !important;
          max-width: none !important;
          min-width: 0 !important;
          align-self: stretch !important;
          flex-direction: column !important;
          gap: 12px !important;
          overflow: visible !important;
        }

        .lisonex-configurator [data-configurator-parameters-layout] > * {
          flex-shrink: 0 !important;
        }

        .lisonex-configurator [data-configurator-preview-sticky] {
          order: 1 !important;
          width: 100% !important;
          max-width: none !important;
          min-width: 0 !important;
          position: static !important;
          overflow: visible !important;
        }

        .lisonex-configurator [data-configurator-sidebar] {
          order: 3 !important;
          width: 100% !important;
          max-width: none !important;
          min-width: 0 !important;
          align-self: stretch !important;
          position: relative !important;
          z-index: 1 !important;
        }

        .lisonex-configurator [data-configurator-preview-panel] {
          display: block !important;
          width: 100% !important;
          height: auto !important;
          max-width: none !important;
          min-width: 0 !important;
          overflow: visible !important;
          position: relative !important;
        }

        .lisonex-configurator [data-configurator-preview-canvas] {
          width: 100% !important;
          max-width: none !important;
          min-width: 0 !important;
          height: min(44svh, 330px) !important;
          min-height: 260px !important;
        }

        .lisonex-configurator [data-configurator-view-presets] {
          bottom: 12px !important;
          right: 12px !important;
          z-index: 25 !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] {
          position: static !important;
          inset: auto !important;
          z-index: auto !important;
          transform: none !important;
          width: 100% !important;
          margin: 0 !important;
          max-height: none !important;
          flex: 0 0 auto !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] > div:first-child {
          height: auto !important;
          min-height: 74px !important;
          padding: 10px 12px !important;
          align-items: center !important;
        }

        .lisonex-configurator
          [data-configurator-preview-summary]
          > div:first-child
          > div:first-child {
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          gap: 8px 10px !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] p {
          font-size: 9px !important;
          line-height: 1.1 !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] strong {
          margin-top: 2px !important;
          font-size: 12px !important;
          line-height: 1.12 !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] button {
          height: 34px !important;
          width: 34px !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] dl {
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 8px 14px !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] dl > div {
          display: block !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] dt,
        .lisonex-configurator [data-configurator-preview-summary] dd {
          max-width: none !important;
          text-align: left !important;
          font-size: 12px !important;
          line-height: 1.25 !important;
        }

        .lisonex-configurator [data-configurator-sidebar] > div {
          width: 100% !important;
          max-width: none !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          padding: 16px !important;
        }

        .lisonex-configurator [data-configurator-sidebar] [data-configurator-sidebar-section-nav] {
          display: none !important;
        }

        .lisonex-configurator [data-configurator-mobile-section-nav] {
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 8px !important;
          order: 2 !important;
          width: 100% !important;
          max-width: none !important;
          min-width: 0 !important;
          position: relative !important;
          z-index: 2 !important;
          margin: 0 !important;
        }

        .lisonex-configurator [data-configurator-sidebar-section-nav] {
          grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
          gap: 8px !important;
        }

        .lisonex-configurator [data-configurator-sidebar-shortcut] {
          min-height: 50px !important;
          justify-content: flex-start !important;
          padding: 8px 10px !important;
        }

        .lisonex-configurator [data-configurator-sidebar-shortcut] > span:first-child {
          display: grid !important;
          height: 32px !important;
          width: 32px !important;
        }

        .lisonex-configurator [data-configurator-sidebar-shortcut] > span:last-child {
          display: block !important;
        }

        .lisonex-configurator [data-configurator-sidebar-shortcut] strong {
          font-size: 12px !important;
        }

        .lisonex-configurator
          [data-configurator-mobile-section-nav]
          [data-configurator-sidebar-shortcut]
          > span:last-child
          > span {
          display: none !important;
        }

        .lisonex-configurator [data-configurator-bottom-actions] {
          flex: 0 0 auto !important;
          padding: 10px 12px !important;
        }

        .lisonex-configurator [data-configurator-bottom-actions] > div {
          display: grid !important;
          grid-template-columns: minmax(0, 1fr) !important;
          gap: 8px !important;
        }

        .lisonex-configurator [data-configurator-bottom-actions] > div > div {
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 8px !important;
        }

        .lisonex-configurator [data-configurator-bottom-actions] button {
          min-width: 0 !important;
          height: 42px !important;
          justify-content: center !important;
          padding-left: 10px !important;
          padding-right: 10px !important;
          font-size: 12px !important;
          line-height: 1.1 !important;
        }

        .lisonex-configurator [data-configurator-bottom-actions] svg {
          height: 16px !important;
          width: 16px !important;
        }
      }

      @media (max-width: 520px) {
        .lisonex-configurator {
          --lx-flow-content-height: calc(100svh - 74px - 112px);
        }

        .lisonex-configurator > header {
          padding: 10px 10px !important;
        }

        .lisonex-configurator > header button span {
          max-width: 64px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .lisonex-configurator [data-configurator-preview-canvas] {
          height: min(42svh, 300px) !important;
          min-height: 250px !important;
        }

        .lisonex-configurator
          [data-configurator-preview-summary]
          > div:first-child
          > div:first-child {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }

        .lisonex-configurator [data-configurator-preview-summary] dl {
          grid-template-columns: minmax(0, 1fr) !important;
        }

        .lisonex-configurator [data-configurator-sidebar-section-nav] {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }

        .lisonex-configurator [data-configurator-mobile-section-nav] {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }
      }

      @media (min-width: 1280px) {
        .lisonex-configurator [data-configurator-parameters-layout] {
          grid-template-columns: minmax(0, 1fr) minmax(360px, 500px) !important;
          justify-content: center !important;
          height: var(--lx-flow-content-height) !important;
          max-height: var(--lx-flow-content-height) !important;
          min-height: 0 !important;
          overflow: hidden !important;
        }

        .lisonex-configurator [data-configurator-flow-content='parameters'] {
          overflow: hidden !important;
        }

        .lisonex-configurator [data-configurator-preview-sticky] {
          position: sticky !important;
          top: 124px !important;
          height: var(--lx-flow-content-height) !important;
          max-height: var(--lx-flow-content-height) !important;
          min-height: 0 !important;
          overflow: hidden !important;
        }

        .lisonex-configurator [data-configurator-preview-panel] {
          height: 100% !important;
          max-height: 100% !important;
        }

        .lisonex-configurator [data-configurator-metrics] {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .lisonex-configurator [data-configurator-sidebar] {
          height: var(--lx-flow-content-height) !important;
          max-height: var(--lx-flow-content-height) !important;
          min-height: 0 !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          scrollbar-color: var(--lx-accent) rgba(232, 117, 0, 0.12);
          scrollbar-gutter: stable;
          scrollbar-width: thin;
        }
      }

      @media (min-width: 1800px) {
        .lisonex-configurator [data-configurator-parameters-layout] {
          grid-template-columns: minmax(0, 1fr) minmax(430px, 540px) !important;
        }
      }

      @media (min-width: 1900px) {
        .lisonex-configurator [data-configurator-metrics] {
          grid-template-columns: repeat(6, minmax(0, 1fr));
        }
      }
    `}</style>
  );
}

function Stepper({
  currentStep,
  isSubmitted = false,
  maxVisitedStep,
  onStepSelect,
}: {
  currentStep: number;
  isSubmitted?: boolean;
  maxVisitedStep: number;
  onStepSelect: (step: number) => void;
}) {
  const { t } = useTranslations();
  const rawActiveDetails =
    isSubmitted && currentStep === 4
      ? SUBMITTED_SUMMARY_STEP_DETAILS
      : (CONFIGURATOR_STEP_DETAILS[currentStep] ?? CONFIGURATOR_STEP_DETAILS[1]);
  const activeDetails = {
    title:
      rawActiveDetails.title === 'text4085' || rawActiveDetails.title === 'text4089'
        ? t(rawActiveDetails.title)
        : rawActiveDetails.title,
    subtitle:
      rawActiveDetails.subtitle === 'text4086'
        ? `${t('text4086')}.`
        : rawActiveDetails.subtitle,
  };
  const progress = Math.min(100, Math.max(0, currentStep * 20));
  const maxReachableStep = Math.min(CONFIGURATOR_STEPS.length, maxVisitedStep + 1);

  return (
    <aside
      data-configurator-stepper
      className="flex flex-col bg-[#071d2b] py-2 lg:sticky lg:top-[128px] lg:min-h-[calc(100vh-190px)]"
    >
      <div className="mb-1 shrink-0">
        <h1 className="text-[26px] font-semibold leading-[1.08] tracking-[-0.02em] text-white lg:text-[32px]">
          {activeDetails.title}
        </h1>
        <p className="text-white/72 mt-3 h-[4.125rem] max-w-[340px] overflow-hidden text-base leading-snug">
          {activeDetails.subtitle || ' '}
        </p>
      </div>

      <div data-configurator-stepper-steps className="min-h-0 flex-1 overflow-y-auto pr-2">
        <div className="grid gap-0">
          {CONFIGURATOR_STEPS.map((step, index) => {
            const isDone = currentStep > step.id;
            const isActive = currentStep === step.id;
            const isReachable = step.id <= maxReachableStep;
            const canNavigate = isReachable && !isActive && step.id !== 5;
            const stepLabel =
              isSubmitted && step.id === 4
                ? t('text4089')
                : step.label === 'text4085'
                  ? t('text4085')
                  : step.label;
            return (
              <div
                key={step.id}
                data-configurator-step-item
                data-current={isActive ? 'true' : undefined}
                className="grid grid-cols-[40px_1fr] gap-4"
              >
                <button
                  type="button"
                  disabled={!canNavigate}
                  onClick={() => onStepSelect(step.id)}
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center text-sm font-semibold transition',
                    isDone && 'bg-white/35 text-white',
                    isActive && 'bg-white text-[#071d2b]',
                    !isDone &&
                      !isActive &&
                      'border border-dashed border-white/25 bg-transparent text-white/70',
                    canNavigate && 'hover:bg-white hover:text-[#071d2b]',
                    !canNavigate && 'cursor-default',
                  )}
                  aria-label={
                    canNavigate
                      ? `Prejsť späť na krok ${step.id}: ${stepLabel}`
                      : `Krok ${step.id}: ${stepLabel}`
                  }
                >
                  {isDone ? <Check className="h-6 w-6" /> : step.id}
                </button>
                <div data-configurator-step-label-wrap className="pb-5">
                  <button
                    type="button"
                    disabled={!canNavigate}
                    onClick={() => onStepSelect(step.id)}
                    data-configurator-step-label
                    className={cn(
                      'pt-2 text-left text-base font-semibold transition',
                      (isActive || isDone) && 'text-white',
                      !isActive && !isDone && 'text-white/55',
                      canNavigate && 'hover:text-white',
                      !canNavigate && 'cursor-default',
                    )}
                  >
                    {step.id === 1
                      ? 'Vyberte typ výrobku'
                      : step.id === 2
                        ? 'Vyberte typ stroja'
                        : step.id === 3
                          ? 'Parametre výrobku'
                          : stepLabel}
                  </button>
                </div>
                {index < CONFIGURATOR_STEPS.length - 1 && (
                  <span className="-mt-5 ml-5 h-5 w-px bg-white/20" aria-hidden="true" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="hidden shrink-0 border-t border-white/10 pt-5 lg:block">
        <div className="h-2 bg-white/25">
          <div className="h-full bg-[#4bd866]" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-3 text-sm font-semibold text-white">{progress}% hotovo</p>
      </div>
    </aside>
  );
}

function HeroIntro() {
  return (
    <div className="mx-auto max-w-[760px] px-5 pb-[clamp(20px,3vh,34px)] text-center">
      <h1 className="text-[clamp(38px,4.6vh,58px)] font-semibold leading-[1.02] tracking-normal text-white">
        {CONFIGURATOR_HERO.title}
      </h1>
      <p className="mx-auto mt-[clamp(10px,1.5vh,16px)] max-w-[520px] text-base leading-snug text-white/65 sm:text-lg">
        {CONFIGURATOR_HERO.subtitle}
      </p>
    </div>
  );
}

function Dashboard({
  onStart,
  onOpenConfiguration,
  dashboardConfigurations,
  authToken,
  locale,
}: {
  onStart: () => void;
  onOpenConfiguration: (configuration: StoreDashboardConfiguration) => void;
  dashboardConfigurations?: StoreDashboardConfiguration[];
  authToken?: string | null;
  locale?: string;
}) {
  const { t } = useTranslations();
  const sentLabel = t('text4103');
  const hasInitialDashboardConfigurations = Array.isArray(dashboardConfigurations);
  const [dashboardState, setDashboardState] = useState<DashboardState>(() =>
    hasInitialDashboardConfigurations
      ? buildDashboardState(dashboardConfigurations ?? [], sentLabel)
      : {
          drafts: [],
          quotes: [],
          isLoading: true,
          error: null,
        },
  );

  useEffect(() => {
    if (hasInitialDashboardConfigurations) return;

    let isMounted = true;

    loadDashboardState(authToken, locale, sentLabel)
      .then(nextState => {
        if (isMounted) setDashboardState(nextState);
      })
      .catch(() => {
        if (!isMounted) return;
        setDashboardState({
          drafts: [],
          quotes: [],
          isLoading: false,
          error: 'Konfigurácie sa nepodarilo načítať.',
        });
      });

    return () => {
      isMounted = false;
    };
  }, [authToken, hasInitialDashboardConfigurations, locale, sentLabel]);

  useEffect(() => {
    if (!hasInitialDashboardConfigurations) return;
    setDashboardState(buildDashboardState(dashboardConfigurations ?? [], sentLabel));
  }, [dashboardConfigurations, hasInitialDashboardConfigurations, sentLabel]);

  return (
    <section
      data-configurator-dashboard
      className="flex flex-1 flex-col justify-start overflow-y-auto overflow-x-hidden px-4 py-[clamp(22px,4.8vh,64px)] sm:px-6 lg:justify-center lg:px-8"
    >
      <HeroIntro />
      <div data-configurator-dashboard-grid className="mx-auto grid w-full max-w-[1500px]">
        <Panel className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between lg:p-8">
          <div>
            <h2 className="text-2xl font-semibold tracking-normal lg:text-3xl">
              Začnite novú konfiguráciu
            </h2>
            <p className="mt-2 text-base text-white/70 lg:text-lg">
              4 jednoduché kroky · ~3 minúty
            </p>
          </div>
          <Button
            className="h-12 rounded-none bg-[#e87500] px-7 text-white hover:bg-[#cf6800] lg:h-14"
            onClick={onStart}
          >
            {t('text4066')}
          </Button>
        </Panel>

        <div data-configurator-dashboard-lists className="grid lg:grid-cols-2">
          <DashboardList
            title={t('text4095')}
            label="Pokračovať"
            items={dashboardState.drafts}
            isLoading={dashboardState.isLoading}
            emptyText="Zatiaľ nemáte rozpracované konfigurácie."
            errorText={dashboardState.error}
            onOpen={onOpenConfiguration}
          />
          <DashboardList
            title="Odoslané konfigurácie"
            label="Detail"
            items={dashboardState.quotes}
            isLoading={dashboardState.isLoading}
            emptyText="Zatiaľ nemáte odoslané konfigurácie."
            errorText={dashboardState.error}
            onOpen={onOpenConfiguration}
          />
        </div>
      </div>
    </section>
  );
}

function DashboardList({
  title,
  label,
  items,
  isLoading,
  emptyText,
  errorText,
  onOpen,
}: {
  title: string;
  label: string;
  items: ReadonlyArray<DashboardItem>;
  isLoading?: boolean;
  emptyText: string;
  errorText?: string | null;
  onOpen: (configuration: StoreDashboardConfiguration) => void;
}) {
  const { t } = useTranslations();
  const [visibleCount, setVisibleCount] = useState(10);
  const visibleItems = items.slice(0, visibleCount);
  const hasMoreItems = items.length > visibleItems.length;

  useEffect(() => {
    setVisibleCount(10);
  }, [items]);

  return (
    <Panel className="p-5 lg:p-7">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold tracking-normal lg:text-2xl">{title}</h2>
      </div>
      <div
        data-configurator-dashboard-list-scroll
        className="grid max-h-[264px] gap-3 overflow-y-auto pr-2"
      >
        {isLoading && (
          <div className="flex min-h-[76px] items-center bg-[#082033] px-5 text-sm font-semibold text-white/65">
            {t('text4092')}
          </div>
        )}
        {!isLoading && errorText && (
          <div className="flex min-h-[76px] items-center border border-[#e87500]/35 bg-[#082033] px-5 text-sm font-semibold text-[#ffb16a]">
            {errorText}
          </div>
        )}
        {!isLoading && !errorText && !items.length && (
          <div className="flex min-h-[76px] items-center bg-[#082033] px-5 text-sm font-semibold text-white/55">
            {emptyText}
          </div>
        )}
        {visibleItems.map(item => (
          <button
            key={item.id}
            className="grid grid-cols-[52px_1fr_auto] items-center gap-4 bg-[#082033] p-3 text-left text-white transition hover:bg-[#0b2a42]"
            onClick={() => onOpen(item.configuration)}
          >
            <DashboardConfigurationPreview configuration={item.configuration} />
            <span>
              <span className="block text-base font-semibold">{item.title}</span>
              <span className="block text-sm text-white/55">{item.subtitle}</span>
            </span>
            <span className="hidden items-center gap-4 sm:flex">
              {(item.step || item.status) && (
                <span
                  className={cn(
                    'rounded-full border px-4 py-2 text-xs',
                    item.statusTone === 'success'
                      ? 'border-[#2fb960]/20 bg-[#2fb960]/20 text-[#63e486]'
                      : 'border-white/15 bg-white/5 text-white/65',
                  )}
                >
                  {item.step ?? item.status}
                </span>
              )}
              <span className="flex items-center gap-2 font-semibold text-[#e87500]">
                {label} <ArrowRight className="h-4 w-4" />
              </span>
            </span>
          </button>
        ))}
      </div>
      {!isLoading && !errorText && hasMoreItems && (
        <button
          className="mt-3 flex h-12 w-full items-center justify-center gap-2 border border-white/10 bg-[#0b2a42] text-sm font-semibold text-[#e87500] transition hover:border-[#e87500]/45 hover:bg-[#0e344f]"
          onClick={() => setVisibleCount(count => count + 10)}
        >
          Zobraziť viac <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </Panel>
  );
}

function ProductStep({
  config,
  update,
}: {
  config: ConfiguratorState;
  update: (patch: Partial<ConfiguratorState>) => void;
}) {
  const { t } = useTranslations();
  const products = getProductOptions();

  return (
    <StepShell
      title="Vyberte typ výrobku"
      subtitle="Začneme tým najdôležitejším - povedzte nám, aký výrobok si želáte konfigurovať."
    >
      {products.length === 0 ? (
        <div
          data-configurator-product-step
          className="mx-auto flex min-h-[360px] w-full max-w-[1180px] items-center justify-center bg-[#15354d] px-6 py-12 text-center shadow-[0_24px_64px_rgba(0,0,0,0.18)]"
        >
          <div className="max-w-[520px]">
            <p className="text-2xl font-semibold tracking-normal text-white">
              {t('text4096')}
            </p>
            <p className="mt-3 text-base leading-snug text-white/65">
              Po pridaní a aktivovaní výrobkov v admine sa zobrazia v tomto kroku.
            </p>
          </div>
        </div>
      ) : (
        <div
          data-configurator-product-step
          className="mx-auto grid w-full max-w-[1180px] grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
        >
          {products.map(product => {
            const active = product.id === config.productTypeId;

            return (
              <button
                data-configurator-product-card
                key={product.id}
                onClick={() => update(buildNamedProductPatch(product.id, config))}
                className={cn(
                  'flex min-h-[330px] flex-col overflow-hidden text-left shadow-[0_24px_64px_rgba(0,0,0,0.18)] transition [container-type:inline-size] hover:-translate-y-1',
                  active
                    ? 'bg-[#e87500] ring-0 hover:bg-[#e87500]'
                    : 'bg-[#082033] hover:bg-[#0c2a42]',
                )}
              >
                <div
                  data-configurator-product-card-media
                  className="mb-5 flex min-h-0 items-center justify-center bg-white"
                  style={{ height: 'clamp(175px, 62cqw, 248px)' }}
                >
                  <ProductTypePreview3D
                    config={buildProductPreviewConfig(product.id)}
                    variant="card"
                  />
                </div>
                <span className="flex flex-1 flex-col px-6 pb-6 pt-6">
                  <span className="text-xl font-semibold tracking-normal text-white">
                    {product.name}
                  </span>
                  <span className="mt-2 min-h-[38px] text-sm leading-snug text-white/70">
                    {product.description}
                  </span>
                  <span
                    className={cn(
                      'mt-5 flex h-11 w-fit items-center gap-3 rounded-full px-4 text-sm font-semibold',
                      active ? 'bg-white text-[#071d2b]' : 'bg-white/10 text-white',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded-full border',
                        active
                          ? 'border-[#071d2b] bg-[#071d2b] text-white'
                          : 'border-white/35 bg-white',
                      )}
                    >
                      {active && <Check className="h-3 w-3" />}
                    </span>
                    {active ? 'Vybraté' : 'Vybrať'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </StepShell>
  );
}
function MachineStep({
  config,
  update,
}: {
  config: ConfiguratorState;
  update: (patch: Partial<ConfiguratorState>) => void;
}) {
  const data = getConfiguratorDataForProduct(config.productTypeId);
  const machines = data.machines;

  useEffect(() => {
    if (!machines.length || machines.some(machine => machine.id === config.machineId)) return;
    update({ machineId: machines[0].id });
  }, [config.machineId, machines, update]);

  return (
    <StepShell title="Vyberte typ stroja" subtitle="Vyberte preferovaný stroj zo zoznamu.">
      <div
        data-configurator-machine-step
        className="mx-auto grid w-full max-w-[1180px] grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
      >
        {machines.length === 0 && (
          <div className="col-span-full border border-white/10 bg-[#082033] px-6 py-8 text-center text-white/70">
            {'Pre tento výrobok zatiaľ nie sú dostupné žiadne stroje.'}
          </div>
        )}
        {machines.map(machine => {
          const active = machine.id === config.machineId;
          return (
            <button
              data-configurator-machine-card
              key={machine.id}
              onClick={() => update({ machineId: machine.id })}
              className={cn(
                'overflow-hidden text-left shadow-[0_24px_64px_rgba(0,0,0,0.18)] transition hover:-translate-y-1',
                active
                  ? 'bg-[#e87500] ring-0 hover:bg-[#e87500]'
                  : 'bg-[#082033] hover:bg-[#0c2a42]',
              )}
            >
              <Image
                data-configurator-machine-card-media
                src={machine.image}
                alt={machine.name}
                width={1365}
                height={750}
                className="aspect-[1.82] w-full object-cover"
                priority
              />
              <div className="p-5">
                <h3 className="text-xl font-semibold tracking-normal">{machine.name}</h3>
                <p className="mt-1 text-sm text-white/65">{machine.description}</p>
                <span
                  className={cn(
                    'mt-4 flex h-10 w-fit items-center gap-3 rounded-full px-4 text-sm font-semibold',
                    active ? 'bg-white text-[#071d2b]' : 'bg-white/10 text-white',
                  )}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-black">
                    <Check className="h-3 w-3" />
                  </span>
                  {active ? 'Vybraté' : 'Vybrať'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </StepShell>
  );
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function roundGeometryMm(value: number) {
  return Math.round(value * 1000) / 1000;
}

function clampHoleEditorPosition(
  config: ConfiguratorState,
  position: { xMm: number; yMm: number },
  diameterMm?: number,
) {
  void diameterMm;

  return {
    xMm: roundGeometryMm(clampNumber(position.xMm, 0, config.widthMm)),
    yMm: roundGeometryMm(clampNumber(position.yMm, 0, config.lengthMm)),
  };
}

function shouldResetAutoHolePositions(config: ConfiguratorState) {
  const positions = config.holePositions;
  if (!positions?.length) return false;

  const automaticHoles = getHoleLayout({ ...config, holePositions: [] });
  if (positions.length < automaticHoles.length) return false;

  return automaticHoles.every((hole, index) => {
    const position = positions[index];
    return (
      position && Math.abs(position.xMm - hole.xMm) < 0.1 && Math.abs(position.yMm - hole.yMm) < 0.1
    );
  });
}
function getUniformNumber(values: number[]) {
  if (!values.length) return null;
  const rounded = values.map(roundGeometryMm);
  return new Set(rounded).size === 1 ? rounded[0] : null;
}

function getUniformString<T extends string>(values: T[]) {
  if (!values.length) return null;
  return new Set(values).size === 1 ? values[0] : null;
}

function NumberControl({
  label,
  value,
  placeholder,
  unit = 'mm',
  onChange,
}: {
  label: string;
  value: number | null;
  placeholder?: string;
  unit?: string;
  onChange: (value: number) => void;
}) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));

  useEffect(() => {
    setDraft(value === null ? '' : String(value));
  }, [value]);

  const commit = () => {
    const parsed = Number(draft.replace(',', '.'));
    if (Number.isFinite(parsed)) onChange(parsed);
    else setDraft(value === null ? '' : String(value));
  };

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-white/55">{label}</span>
      <span className="flex h-10 items-center rounded-lg border border-white/15 bg-[#092031] pr-2 focus-within:border-white/45">
        <input
          value={draft}
          placeholder={placeholder}
          inputMode="decimal"
          onBlur={commit}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
          className="h-full min-w-0 flex-1 rounded-lg bg-transparent px-3 text-sm font-semibold text-white outline-none placeholder:text-white/35"
        />
        <span className="text-xs font-semibold text-white/55">{unit}</span>
      </span>
    </label>
  );
}

function SlotOrientationControl({
  value,
  onChange,
  label = 'Orientácia otvoru',
}: {
  value: HoleSlotOrientation;
  onChange: (value: HoleSlotOrientation) => void;
  label?: string;
}) {
  const items: Array<{ id: HoleSlotOrientation; label: string; lineClassName: string }> = [
    { id: 'horizontal', label: 'Vodorovne', lineClassName: 'h-1.5 w-8' },
    { id: 'vertical', label: 'Zvislo', lineClassName: 'h-8 w-1.5' },
    { id: 'custom', label: 'Uhol', lineClassName: 'h-1.5 w-8 rotate-[-35deg]' },
  ];

  return (
    <div>
      <span className="mb-2 block text-xs font-semibold text-white/55">{label}</span>
      <div className="grid grid-cols-3 gap-2">
        {items.map(item => {
          const active = value === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item.id)}
              className={cn(
                'grid h-20 place-items-center gap-1 rounded-xl border bg-[#092031] text-xs font-semibold transition',
                active
                  ? 'border-white !bg-white !text-[#071d2b] shadow-[0_8px_20px_rgba(0,0,0,0.12)]'
                  : 'border-white/15 text-white/70 hover:border-white/45 hover:text-white',
              )}
            >
              <span
                className={cn(
                  'grid h-9 w-12 place-items-center rounded-lg',
                  active ? '!bg-[#eef3f6]' : 'bg-white/10',
                )}
              >
                <span
                  className={cn(
                    'block rounded-full transition',
                    active ? '!bg-[#071d2b]' : 'bg-current',
                    item.lineClassName,
                  )}
                />
              </span>
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HoleSelectionPanel({
  config,
  update,
  selectedHoleIndexes,
  onHoleSelectionChange,
  gridStepMm,
  setGridStepMm,
}: {
  config: ConfiguratorState;
  update: (patch: Partial<ConfiguratorState>) => void;
  selectedHoleIndexes: number[];
  activeHoleIndex: number | null;
  onHoleSelectionChange: (indexes: number[], activeIndex: number | null) => void;
  gridStepMm: number;
  setGridStepMm: (value: number) => void;
}) {
  const { t } = useTranslations();
  const limits = useMemo(() => getEffectiveLimits(config), [config]);
  const holes = useMemo(() => getHoleLayout(config), [config]);
  const selectedIndexes = selectedHoleIndexes.filter(index => holes[index]);
  const selectedHoles = selectedIndexes.map(index => holes[index]);
  const selectedLabel =
    selectedIndexes.length === 1
      ? `Otvor #${selectedIndexes[0] + 1}`
      : selectedIndexes.length + ' otvorov';
  const xValue = getUniformNumber(selectedHoles.map(hole => hole.xMm));
  const yValue = getUniformNumber(selectedHoles.map(hole => hole.yMm));
  const diameterValue = getUniformNumber(selectedHoles.map(hole => hole.diameterMm));
  const shapeValue = getUniformString(selectedHoles.map(hole => hole.shape));
  const slotLengthValue = getUniformNumber(selectedHoles.map(hole => hole.slotLengthMm));
  const slotOrientationValue = getUniformString(selectedHoles.map(hole => hole.slotOrientation));
  const slotAngleValue = getUniformNumber(selectedHoles.map(hole => hole.slotAngleDeg));
  const selectedShape = shapeValue ?? config.holeShape;
  const selectedShapeUsesLength = isLengthBasedHoleShape(selectedShape);

  const commitArrays = (
    positions: Array<{ xMm: number; yMm: number }>,
    diameters: number[],
    shapes: HoleShape[],
    slotLengths: number[],
    slotOrientations: HoleSlotOrientation[],
    slotAngles: number[],
  ) => {
    const uniqueDiameters = new Set(diameters.map(value => Math.round(value * 1000)));
    const uniqueShapes = new Set(shapes);
    const uniqueSlotLengths = new Set(slotLengths.map(value => Math.round(value * 1000)));
    const uniqueSlotOrientations = new Set(slotOrientations);
    const uniqueSlotAngles = new Set(slotAngles.map(value => Math.round(value * 1000)));
    update({
      holePositions: positions,
      holeDiameters: diameters,
      holeShapes: shapes,
      holeSlotLengths: slotLengths,
      holeSlotOrientations: slotOrientations,
      holeSlotAnglesDeg: slotAngles,
      ...(uniqueDiameters.size === 1 ? { holeDiameterMm: diameters[0] } : {}),
      ...(uniqueShapes.size === 1 ? { holeShape: shapes[0] } : {}),
      ...(uniqueSlotLengths.size === 1 ? { holeSlotLengthMm: slotLengths[0] } : {}),
      ...(uniqueSlotOrientations.size === 1 ? { holeSlotOrientation: slotOrientations[0] } : {}),
      ...(uniqueSlotAngles.size === 1 ? { holeSlotAngleDeg: slotAngles[0] } : {}),
    });
  };

  const editSelected = (
    editor: (
      hole: (typeof holes)[number],
      index: number,
    ) => Partial<{
      xMm: number;
      yMm: number;
      diameterMm: number;
      shape: HoleShape;
      slotLengthMm: number;
      slotOrientation: HoleSlotOrientation;
      slotAngleDeg: number;
    }>,
  ) => {
    const selectedSet = new Set(selectedIndexes);
    const nextPositions = holes.map(hole => ({ xMm: hole.xMm, yMm: hole.yMm }));
    const nextDiameters = holes.map(hole => hole.diameterMm);
    const nextShapes = holes.map(hole => hole.shape);
    const nextSlotLengths = holes.map(hole => hole.slotLengthMm);
    const nextSlotOrientations = holes.map(hole => hole.slotOrientation);
    const nextSlotAngles = holes.map(hole => hole.slotAngleDeg);

    holes.forEach((hole, index) => {
      if (!selectedSet.has(index)) return;
      const patch = editor(hole, index);
      const diameterMm = clampNumber(
        patch.diameterMm ?? hole.diameterMm,
        limits.holeDiameterMm.min,
        limits.holeDiameterMm.max,
      );
      nextDiameters[index] = diameterMm;
      nextShapes[index] = patch.shape ?? hole.shape;
      nextSlotOrientations[index] = patch.slotOrientation ?? hole.slotOrientation;
      nextSlotAngles[index] =
        typeof patch.slotAngleDeg === 'number'
          ? clampNumber(
              patch.slotAngleDeg,
              limits.holeSlotAngleDeg.min,
              limits.holeSlotAngleDeg.max,
            )
          : patch.slotOrientation === 'horizontal'
            ? 0
            : patch.slotOrientation === 'vertical'
              ? 90
              : hole.slotAngleDeg;
      nextSlotLengths[index] = clampNumber(
        Math.max(patch.slotLengthMm ?? hole.slotLengthMm, diameterMm),
        Math.max(limits.holeSlotLengthMm.min, diameterMm),
        limits.holeSlotLengthMm.max,
      );
      nextPositions[index] = clampHoleEditorPosition(
        config,
        {
          xMm: patch.xMm ?? hole.xMm,
          yMm: patch.yMm ?? hole.yMm,
        },
        diameterMm,
      );
    });

    commitArrays(
      nextPositions,
      nextDiameters,
      nextShapes,
      nextSlotLengths,
      nextSlotOrientations,
      nextSlotAngles,
    );
  };

  return (
    <div className="grid gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <strong className="block text-base">Parametre otvoru</strong>
          <p className="mt-1 text-xs leading-snug text-white/60">
            {selectedIndexes.length ? selectedLabel : 'Vyberte otvor v 2D nahlade.'}
          </p>
        </div>
        {selectedIndexes.length > 0 && (
          <button
            type="button"
            className="rounded-full border border-white/15 bg-[#092031] px-3 py-1.5 text-xs font-semibold text-white transition hover:border-[#e87500] hover:text-[#e87500]"
            onClick={() => onHoleSelectionChange([], null)}
          >
            {t('text3628')}
          </button>
        )}
      </div>

      {selectedIndexes.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <NumberControl
              label={holePrimaryDimensionLabel(selectedShape)}
              value={diameterValue}
              placeholder="rozne"
              onChange={value => editSelected(() => ({ diameterMm: value }))}
            />
            <NumberControl
              label="X"
              value={xValue}
              placeholder="rozne"
              onChange={value => editSelected(() => ({ xMm: value }))}
            />
            <NumberControl
              label="Y"
              value={yValue}
              placeholder="rozne"
              onChange={value => editSelected(() => ({ yMm: value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <SelectField
              label="Typ otvoru"
              value={shapeValue ?? config.holeShape}
              onChange={value => editSelected(() => ({ shape: value as HoleShape }))}
              items={HOLE_SHAPE_OPTIONS}
            />
            {selectedShapeUsesLength && (
              <NumberControl
                label={holeSecondaryDimensionLabel(selectedShape)}
                value={slotLengthValue}
                placeholder="rôzne"
                onChange={value => editSelected(() => ({ slotLengthMm: value }))}
              />
            )}
          </div>
          {selectedShapeUsesLength && (
            <div className="grid gap-3">
              <SlotOrientationControl
                value={(slotOrientationValue ?? config.holeSlotOrientation) as HoleSlotOrientation}
                onChange={value =>
                  editSelected(hole => ({
                    slotOrientation: value,
                    slotAngleDeg:
                      value === 'horizontal' ? 0 : value === 'vertical' ? 90 : hole.slotAngleDeg,
                  }))
                }
              />
              {(slotOrientationValue ?? config.holeSlotOrientation) === 'custom' && (
                <NumberControl
                  label="Uhol otvoru"
                  value={slotAngleValue}
                  placeholder="rôzne"
                  unit="°"
                  onChange={value => editSelected(() => ({ slotAngleDeg: value }))}
                />
              )}
            </div>
          )}

          <div className="max-w-[180px] pt-1">
            <NumberControl
              label="Raster"
              value={gridStepMm}
              unit="mm"
              onChange={value => setGridStepMm(clampNumber(value, 0.001, 50))}
            />
          </div>
        </>
      )}
    </div>
  );
}

function CornerSelectionPanel({
  config,
  update,
  limits,
  onBack,
}: {
  config: ConfiguratorState;
  update: (patch: Partial<ConfiguratorState>) => void;
  limits: ReturnType<typeof getEffectiveLimits>;
  onBack: () => void;
}) {
  const maxCornerSize = Math.min(limits.cornerSizeMm.max, config.widthMm / 2, config.lengthMm / 2);
  const cornerLimit = {
    ...limits.cornerSizeMm,
    max: Math.max(limits.cornerSizeMm.min, maxCornerSize),
  };

  const updateCornerType = (corner: CornerFieldKey, value: string) => {
    const type = value as CornerEditType;
    const sizeKey = cornerSizeKey(corner);
    const xKey = cornerChamferXKey(corner);
    const yKey = cornerChamferYKey(corner);
    const previousSize = getConfigNumber(config, sizeKey);
    const previousChamfer = getCornerChamferValues(config, corner);
    const nextSize =
      type === 'square' ? 0 : Math.max(previousSize, previousChamfer.xMm, previousChamfer.yMm, 10);

    update({
      [cornerTypeKey(corner)]: type,
      [sizeKey]: nextSize,
      [xKey]: type === 'square' ? 0 : Math.max(previousChamfer.xMm, nextSize),
      [yKey]: type === 'square' ? 0 : Math.max(previousChamfer.yMm, nextSize),
    } as Partial<ConfiguratorState>);
  };

  const updateSymmetricSize = (corner: CornerFieldKey, value: number) => {
    const size = clampNumber(value, cornerLimit.min, cornerLimit.max);
    update({
      [cornerSizeKey(corner)]: size,
      [cornerChamferXKey(corner)]: size,
      [cornerChamferYKey(corner)]: size,
    } as Partial<ConfiguratorState>);
  };

  const updateIrregularChamfer = (
    corner: CornerFieldKey,
    patch: Partial<{ xMm: number; yMm: number }>,
  ) => {
    const previous = getCornerChamferValues(config, corner);
    const xMm = clampNumber(patch.xMm ?? previous.xMm, cornerLimit.min, cornerLimit.max);
    const yMm = clampNumber(patch.yMm ?? previous.yMm, cornerLimit.min, cornerLimit.max);

    update({
      [cornerSizeKey(corner)]: Math.max(xMm, yMm),
      [cornerChamferXKey(corner)]: xMm,
      [cornerChamferYKey(corner)]: yMm,
    } as Partial<ConfiguratorState>);
  };

  return (
    <div className="grid gap-4">
      <SectionPanelHeader title="Rohy dielu" onBack={onBack} />

      <div className="grid gap-2">
        {CORNER_FIELDS.map(corner => {
          const type = getCornerEditType(config, corner.key);
          const size = getConfigNumber(config, cornerSizeKey(corner.key));
          const chamfer = getCornerChamferValues(config, corner.key);

          return (
            <div
              key={corner.key}
              className="grid gap-3 rounded-2xl border border-white/15 bg-[#092031] p-4"
            >
              <SelectField
                label={corner.label}
                value={type}
                onChange={value => updateCornerType(corner.key, value)}
                items={CORNER_TYPE_OPTIONS}
              />

              {type === 'radius' && (
                <RangeField
                  label="Rádius"
                  unit="mm"
                  value={size}
                  limit={cornerLimit}
                  onChange={value => updateSymmetricSize(corner.key, value)}
                />
              )}

              {type === 'chamfer' && (
                <RangeField
                  label="Seknutie"
                  unit="mm"
                  value={size}
                  limit={cornerLimit}
                  onChange={value => updateSymmetricSize(corner.key, value)}
                />
              )}

              {type === 'chamfer-irregular' && (
                <div className="grid gap-4">
                  <RangeField
                    label="Šírka seknutia"
                    unit="mm"
                    value={chamfer.xMm}
                    limit={cornerLimit}
                    onChange={value => updateIrregularChamfer(corner.key, { xMm: value })}
                  />
                  <RangeField
                    label="Hĺbka seknutia"
                    unit="mm"
                    value={chamfer.yMm}
                    limit={cornerLimit}
                    onChange={value => updateIrregularChamfer(corner.key, { yMm: value })}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ShapeSelectionPanel({
  config,
  update,
  onBack,
}: {
  config: ConfiguratorState;
  update: (patch: Partial<ConfiguratorState>) => void;
  onBack: () => void;
}) {
  const outline = getProductOutlineSettings(config);
  const radiusLimit = {
    min: 0,
    max: Math.max(0, Math.floor(Math.min(config.widthMm, config.lengthMm) / 2)),
    step: 1,
  };
  const sharedCornerRadius = (() => {
    const radii = CORNER_FIELDS.map(corner =>
      getCornerEditType(config, corner.key) === 'radius'
        ? getConfigNumber(config, cornerSizeKey(corner.key))
        : null,
    );
    if (radii.some(value => value === null)) return null;

    const firstRadius = radii[0] ?? 0;
    return radii.every(value => Math.abs((value ?? 0) - firstRadius) < 0.001) ? firstRadius : null;
  })();
  const visibleOuterRadius = sharedCornerRadius ?? outline.outerRadiusMm;
  const updateOuterRadius = (value: number) => {
    const nextRadius = clampNumber(value, radiusLimit.min, radiusLimit.max);
    const nextType = nextRadius > 0 ? 'radius' : 'square';

    update({
      edgeRadiusMm: nextRadius,
      outlineOuterRadiusMm: nextRadius,
      cornerTopLeftType: nextType,
      cornerTopLeftMm: nextRadius,
      cornerTopLeftChamferXMm: nextRadius,
      cornerTopLeftChamferYMm: nextRadius,
      cornerTopRightType: nextType,
      cornerTopRightMm: nextRadius,
      cornerTopRightChamferXMm: nextRadius,
      cornerTopRightChamferYMm: nextRadius,
      cornerBottomRightType: nextType,
      cornerBottomRightMm: nextRadius,
      cornerBottomRightChamferXMm: nextRadius,
      cornerBottomRightChamferYMm: nextRadius,
      cornerBottomLeftType: nextType,
      cornerBottomLeftMm: nextRadius,
      cornerBottomLeftChamferXMm: nextRadius,
      cornerBottomLeftChamferYMm: nextRadius,
    });
  };

  return (
    <div className="grid gap-4">
      <SectionPanelHeader title="Tvar dielu" onBack={onBack} />

      <div className="grid gap-4 rounded-2xl border border-white/15 bg-[#092031] p-4">
        <RangeField
          label="Vonkajšie zaoblenie"
          unit="mm"
          value={visibleOuterRadius}
          limit={radiusLimit}
          onChange={updateOuterRadius}
        />
      </div>
    </div>
  );
}

function InternalCutsPanel({
  config,
  update,
  onBack,
  selectedCutId,
  onSelectCut,
  selectedNotchId,
  onSelectNotch,
  gridStepMm = 1,
}: {
  config: ConfiguratorState;
  update: (patch: Partial<ConfiguratorState>) => void;
  onBack: () => void;
  selectedCutId?: string | null;
  onSelectCut?: (id: string | null) => void;
  selectedNotchId?: string | null;
  onSelectNotch?: (id: string | null) => void;
  gridStepMm?: number;
}) {
  void gridStepMm;
  const selectedDetailRef = useRef<HTMLDivElement | null>(null);
  const cutOverrides = toEditableInternalCuts(config);
  const outline = getProductOutlineSettings(config);
  const outlineNotches = toEditableOutlineNotches(config);
  const snapPositionMm = (value: number, max: number) => {
    const clamped = clampNumber(value, 0, max);
    return roundGeometryMm(clamped);
  };
  const getNotchAxisSize = (edge: OutlineNotchEdge = 'top') =>
    isHorizontalOutlineEdge(edge) ? config.widthMm : config.lengthMm;
  const getNotchDepthAxisSize = (edge: OutlineNotchEdge = 'top') =>
    isHorizontalOutlineEdge(edge) ? config.lengthMm : config.widthMm;
  const getNotchWidthLimit = (edge: OutlineNotchEdge = 'top') => ({
    min: 0,
    max: Math.max(0, Math.floor(getNotchAxisSize(edge) * 0.85)),
    step: 1,
  });
  const getNotchDepthLimit = (edge: OutlineNotchEdge = 'top') => ({
    min: 0,
    max: Math.max(0, Math.floor(getNotchDepthAxisSize(edge) * 0.75)),
    step: 1,
  });
  const getNotchMouthRadiusLimit = (notch: OutlineNotchOverride) => {
    const edge = notch.edge ?? 'top';
    const axisSize = getNotchAxisSize(edge);
    const centerMm =
      notch.centerMm ?? axisSize * Math.min(Math.max(notch.centerRatio ?? 0.5, 0), 1);
    const widthMm = Math.max(0, notch.widthMm ?? outline.centerGapMm);
    const depthMm = Math.max(0, notch.depthMm ?? outline.topNotchDepthMm);
    const leftSpace = centerMm - widthMm / 2;
    const rightSpace = axisSize - (centerMm + widthMm / 2);

    return {
      min: 0,
      max: Math.max(0, Math.floor(Math.min(depthMm * 0.45, leftSpace, rightSpace))),
      step: 1,
    };
  };
  const getNotchEdgeLabel = (edge: OutlineNotchEdge = 'top') =>
    OUTLINE_NOTCH_EDGE_OPTIONS.find(option => option.id === edge)?.label ?? 'Hrana';

  const syncLegacyTopNotch = (notches: OutlineNotchOverride[]) => {
    const firstTopNotch = notches.find(notch => (notch.edge ?? 'top') === 'top');

    return {
      outlineNotches: notches,
      ...(firstTopNotch
        ? {
            outlineCenterGapMm: firstTopNotch.widthMm ?? outline.centerGapMm,
            outlineTopNotchDepthMm: firstTopNotch.depthMm ?? outline.topNotchDepthMm,
          }
        : {}),
    };
  };
  const updateNotches = (nextNotches: OutlineNotchOverride[]) =>
    update(syncLegacyTopNotch(nextNotches));
  const editNotch = (index: number, patch: Partial<OutlineNotchOverride>) => {
    updateNotches(
      outlineNotches.map((notch, notchIndex) =>
        notchIndex === index ? { ...notch, ...patch } : notch,
      ),
    );
  };

  const getNextNotchId = (items = outlineNotches) => {
    const usedIds = new Set(items.map(notch => notch.id));
    let nextIndex = outlineNotches.length + 1;
    while (usedIds.has(`outline-notch-${nextIndex}`)) nextIndex += 1;

    return `outline-notch-${nextIndex}`;
  };

  const removeNotch = (index: number) => {
    const removed = outlineNotches[index];
    if (removed?.id === selectedNotchId) onSelectNotch?.(null);
    updateNotches(outlineNotches.filter((_, notchIndex) => notchIndex !== index));
  };

  const updateCuts = (nextCuts: InternalCutOverride[]) => {
    update({ internalCuts: nextCuts });
  };

  const getNextCutId = (items = cutOverrides) => {
    const usedIds = new Set(items.map(cut => cut.id));
    let nextIndex = cutOverrides.length + 1;
    while (usedIds.has(`cut-${nextIndex}`)) nextIndex += 1;

    return `cut-${nextIndex}`;
  };

  const editCut = (index: number, patch: Partial<InternalCutOverride>) => {
    const nextCuts = cutOverrides.map((cut, cutIndex) =>
      cutIndex === index ? { ...cut, ...patch } : cut,
    );
    updateCuts(nextCuts);
  };

  const addCut = () => {
    const nextIndex = cutOverrides.length + outlineNotches.length + 1;
    const nextCut = {
      id: getNextCutId(),
      label: `Rez ${nextIndex}`,
      shape: 'slot' as const,
      xMm: Math.round(config.widthMm / 2),
      yMm: Math.round(config.lengthMm / 2),
      widthMm: Math.max(4, config.thicknessMm),
      lengthMm: Math.max(28, Math.round(config.lengthMm * 0.24)),
      orientation: 'vertical' as const,
      cornerRadiusMm: Math.max(4, config.thicknessMm) / 2,
    };

    updateCuts([...cutOverrides, nextCut]);
    onSelectNotch?.(null);
    onSelectCut?.(nextCut.id);
  };

  const removeCut = (index: number) => {
    const removed = cutOverrides[index];
    if (removed?.id === selectedCutId) onSelectCut?.(null);
    updateCuts(cutOverrides.filter((_, cutIndex) => cutIndex !== index));
  };

  const convertNotchToCut = (index: number) => {
    const notch = outlineNotches[index];
    if (!notch) return;

    const width = notch.widthMm ?? outline.centerGapMm;
    const depth = notch.depthMm ?? outline.topNotchDepthMm;
    const edge = notch.edge ?? 'top';
    const isHorizontalEdge = isHorizontalOutlineEdge(edge);
    const nextCut: InternalCutOverride = {
      id: getNextCutId(),
      label: notch.label || `Rez ${cutOverrides.length + outlineNotches.length}`,
      shape: 'slot' as const,
      xMm: isHorizontalEdge
        ? (notch.centerMm ?? Math.round(config.widthMm / 2))
        : edge === 'right'
          ? Math.round(config.widthMm - depth / 2)
          : Math.round(depth / 2),
      yMm: isHorizontalEdge
        ? edge === 'bottom'
          ? Math.round(config.lengthMm - depth / 2)
          : Math.round(depth / 2)
        : (notch.centerMm ?? Math.round(config.lengthMm / 2)),
      widthMm: isHorizontalEdge ? depth : width,
      lengthMm: Math.max(width, depth),
      orientation: isHorizontalEdge ? 'horizontal' : 'vertical',
      cornerRadiusMm: Math.min(width, Math.max(width, depth)) / 2,
    };
    const nextNotches = outlineNotches.filter((_, notchIndex) => notchIndex !== index);

    update({
      ...syncLegacyTopNotch(nextNotches),
      internalCuts: [...cutOverrides, nextCut],
    });
    onSelectNotch?.(null);
    onSelectCut?.(nextCut.id);
  };

  const convertCutToNotch = (index: number) => {
    const cut = cutOverrides[index];
    if (!cut) return;

    const cutWidth = cut.widthMm ?? Math.max(8, Math.round(config.widthMm * 0.08));
    const cutLength = cut.lengthMm ?? cutWidth;
    const xMm = cut.xMm ?? config.widthMm / 2;
    const yMm = cut.yMm ?? config.lengthMm / 2;
    const nearestEdge = [
      { edge: 'top' as const, distance: yMm },
      { edge: 'right' as const, distance: config.widthMm - xMm },
      { edge: 'bottom' as const, distance: config.lengthMm - yMm },
      { edge: 'left' as const, distance: xMm },
    ].sort((a, b) => a.distance - b.distance)[0];
    const edge: OutlineNotchEdge = nearestEdge?.edge ?? 'top';
    const isHorizontalEdge = isHorizontalOutlineEdge(edge);
    const depthLimit = getNotchDepthLimit(edge);
    const nextNotch: OutlineNotchOverride = {
      id: getNextNotchId(),
      label: cut.label || `Výrez ${cutOverrides.length + outlineNotches.length}`,
      edge,
      centerMm: isHorizontalEdge ? xMm : yMm,
      widthMm: cutLength,
      depthMm: Math.min(Math.max(cutWidth, nearestEdge?.distance ?? cutWidth), depthLimit.max),
      radiusMm: cut.cornerRadiusMm ?? 0,
      mouthRadiusMm: 0,
    };
    const nextCuts = cutOverrides.filter((_, cutIndex) => cutIndex !== index);

    update({
      internalCuts: nextCuts,
      ...syncLegacyTopNotch([...outlineNotches, nextNotch]),
    });
    onSelectCut?.(null);
    onSelectNotch?.(nextNotch.id);
  };

  const getNotchPreview = (notch: OutlineNotchOverride) => {
    const edge = notch.edge ?? 'top';
    const center = notch.centerMm ?? Math.round(config.widthMm / 2);
    const width = notch.widthMm ?? outline.centerGapMm;
    const depth = notch.depthMm ?? outline.topNotchDepthMm;
    const radius = notch.radiusMm ?? 0;
    const mouthRadius = notch.mouthRadiusMm ?? 0;
    const radiusLabel = radius > 0 ? ` · dnu R${formatNumber(radius, 0)}` : '';
    const mouthRadiusLabel = mouthRadius > 0 ? ` · obvod R${formatNumber(mouthRadius, 0)}` : '';

    return `${getNotchEdgeLabel(edge)} · ${formatNumber(center, 0)} mm · ${formatNumber(width, 0)} × ${formatNumber(
      depth,
      0,
    )} mm${radiusLabel}${mouthRadiusLabel}`;
  };
  const getCutPreview = (cut: InternalCutOverride) => {
    const width = cut.widthMm ?? 0;
    const length = cut.lengthMm ?? width;
    const angle = cut.orientation === 'custom' ? ` · ${formatNumber(cut.angleDeg ?? 45, 0)}°` : '';
    const radius =
      (cut.cornerRadiusMm ?? 0) > 0 ? ` · R${formatNumber(cut.cornerRadiusMm ?? 0, 0)}` : '';
    const size = `${formatNumber(width, 0)} × ${formatNumber(length, 0)} mm`;

    return `X ${formatNumber(cut.xMm ?? 0, 0)} · Y ${formatNumber(cut.yMm ?? 0, 0)} · ${size}${radius}${angle}`;
  };
  const scrollSelectedDetailIntoView = () => {
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      const element = selectedDetailRef.current;
      if (!element) return;

      const preferredParent = element.closest('[data-configurator-sidebar-scroll]');
      const scrollParent =
        preferredParent instanceof HTMLElement &&
        preferredParent.scrollHeight > preferredParent.clientHeight
          ? preferredParent
          : element.closest("[data-configurator-flow-content='parameters']");
      if (!(scrollParent instanceof HTMLElement)) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        return;
      }

      const parentRect = scrollParent.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const targetTop =
        scrollParent.scrollTop +
        elementRect.top -
        parentRect.top -
        parentRect.height / 2 +
        elementRect.height / 2;

      const maxTop = Math.max(0, scrollParent.scrollHeight - scrollParent.clientHeight);

      scrollParent.scrollTo({
        top: Math.min(maxTop, Math.max(0, targetTop)),
        behavior: 'smooth',
      });
    });
  };
  const selectNotch = (id: string | null) => {
    onSelectNotch?.(id);
    if (id) scrollSelectedDetailIntoView();
  };
  const selectCut = (id: string | null) => {
    onSelectCut?.(id);
    if (id) scrollSelectedDetailIntoView();
  };

  useEffect(() => {
    if (!selectedCutId && !selectedNotchId) return;
    if (typeof window === 'undefined') return;

    const frame = window.requestAnimationFrame(() => {
      scrollSelectedDetailIntoView();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedCutId, selectedNotchId]);

  return (
    <div className="grid gap-4">
      <SectionPanelHeader
        title="Rezy / výrezy"
        onBack={onBack}
        onAdd={addCut}
        addLabel="Pridať rez"
      />

      <div className="grid gap-3">
        {outlineNotches.length + cutOverrides.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/15 bg-[#092031] p-5 text-sm font-medium text-white/65">
            Tento diel zatiaľ nemá žiadny rez. Pridaj ho tlačidlom hore.
          </div>
        ) : (
          <div className="grid gap-2">
            {outlineNotches.map(notch => {
              const isSelected = selectedNotchId === notch.id;
              const notchIndex = outlineNotches.findIndex(item => item.id === notch.id);

              return (
                <div
                  key={notch.id}
                  className={cn(
                    'overflow-hidden rounded-xl border bg-[#092031] text-white transition',
                    isSelected
                      ? 'border-[#2f9560] bg-[#0b2a42] shadow-[0_10px_24px_rgba(47,149,96,0.18)]'
                      : 'border-white/15',
                  )}
                >
                  <div className="flex w-full items-center justify-between gap-3 px-3 py-2 transition hover:bg-[#0d2b42]">
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => {
                        if (isSelected) {
                          selectNotch(null);
                          return;
                        }
                        onSelectCut?.(null);
                        selectNotch(notch.id);
                      }}
                    >
                      <span className="block truncate text-sm font-semibold">
                        {notch.label || 'Výrez z obrysu'}
                      </span>
                      <span className="mt-0.5 block truncate text-xs font-medium text-white/60">
                        {getNotchPreview(notch)}
                      </span>
                    </button>
                    {isSelected ? (
                      <button
                        type="button"
                        className={sectionActionButtonClass}
                        onClick={() => removeNotch(notchIndex)}
                        aria-label="Odstrániť výrez z obrysu"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : (
                      <span className="shrink-0 rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/65">
                        Obrysový
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <div
                      ref={selectedDetailRef}
                      className="grid gap-3 border-t border-white/10 p-3"
                      onPointerDownCapture={() => {
                        onSelectCut?.(null);
                        onSelectNotch?.(notch.id);
                      }}
                    >
                      <div className="grid grid-cols-2 gap-1 rounded-full bg-[#071d2b] p-1">
                        <button
                          type="button"
                          className="h-9 rounded-full px-3 text-sm font-semibold text-white/65 transition hover:text-white"
                          onClick={() => convertNotchToCut(notchIndex)}
                        >
                          Vnútorný
                        </button>
                        <button
                          type="button"
                          className="h-9 rounded-full bg-black px-3 text-sm font-semibold text-white shadow-sm"
                        >
                          Obrysový
                        </button>
                      </div>

                      <label className="min-w-0">
                        <span className="mb-1 block text-xs font-semibold text-white/65">
                          Názov
                        </span>
                        <input
                          value={notch.label ?? ''}
                          onChange={event => editNotch(notchIndex, { label: event.target.value })}
                          className="h-10 w-full rounded-lg border border-white/15 bg-[#071d2b] px-3 text-sm font-semibold text-white outline-none focus:border-white/45"
                        />
                      </label>

                      <div>
                        <span className="mb-1 block text-xs font-semibold text-white/65">
                          Hrana výrezu
                        </span>
                        <div className="grid grid-cols-2 gap-2">
                          {OUTLINE_NOTCH_EDGE_OPTIONS.map(edge => {
                            const edgeActive = (notch.edge ?? 'top') === edge.id;

                            return (
                              <button
                                key={edge.id}
                                type="button"
                                className={cn(
                                  'flex h-12 items-center gap-3 rounded-xl border bg-[#071d2b] px-3 text-left text-xs font-semibold text-white transition hover:border-white/45',
                                  edgeActive ? 'border-white' : 'border-white/15',
                                )}
                                onClick={() => editNotch(notchIndex, { edge: edge.id })}
                              >
                                <span
                                  className={cn(
                                    'block h-7 w-9 rounded border border-[#cfcfcf]',
                                    edge.marker,
                                    edgeActive ? 'border-white' : 'border-white/35',
                                  )}
                                />
                                {edge.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
                        <CompactNumberField
                          label="Stred"
                          value={notch.centerMm ?? Math.round(config.widthMm / 2)}
                          onChange={value => editNotch(notchIndex, { centerMm: value })}
                        />
                        <CompactNumberField
                          label="Zaoblenie vnútri"
                          value={notch.radiusMm ?? 0}
                          onChange={value =>
                            editNotch(notchIndex, {
                              radiusMm: clampNumber(
                                value,
                                0,
                                Math.min(notch.widthMm ?? 0, notch.depthMm ?? 0) / 2,
                              ),
                            })
                          }
                        />
                        <CompactNumberField
                          label="Zaoblenie na obvode"
                          value={notch.mouthRadiusMm ?? 0}
                          onChange={value => {
                            const limit = getNotchMouthRadiusLimit(notch);

                            editNotch(notchIndex, {
                              mouthRadiusMm: clampNumber(value, limit.min, limit.max),
                            });
                          }}
                        />
                      </div>

                      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
                        <CompactNumberField
                          label="Šírka"
                          value={notch.widthMm ?? outline.centerGapMm}
                          onChange={value =>
                            editNotch(notchIndex, {
                              widthMm: clampNumber(
                                value,
                                getNotchWidthLimit(notch.edge ?? 'top').min,
                                getNotchWidthLimit(notch.edge ?? 'top').max,
                              ),
                            })
                          }
                        />
                        <CompactNumberField
                          label="Hĺbka"
                          value={notch.depthMm ?? outline.topNotchDepthMm}
                          onChange={value =>
                            editNotch(notchIndex, {
                              depthMm: clampNumber(
                                value,
                                getNotchDepthLimit(notch.edge ?? 'top').min,
                                getNotchDepthLimit(notch.edge ?? 'top').max,
                              ),
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {cutOverrides.map(cut => {
              const isSelected = selectedCutId === cut.id;
              const cutIndex = cutOverrides.findIndex(item => item.id === cut.id);
              const cutOrientation = cut.orientation ?? 'vertical';
              const cutWidth = cut.widthMm ?? 0;
              const cutLength = cut.lengthMm ?? cutWidth;
              const maxCutRadius = Math.max(0, Math.min(cutWidth, cutLength) / 2);

              return (
                <div
                  key={cut.id}
                  className={cn(
                    'overflow-hidden rounded-xl border bg-[#092031] text-white transition',
                    isSelected
                      ? 'border-[#2f9560] bg-[#0b2a42] shadow-[0_10px_24px_rgba(47,149,96,0.18)]'
                      : 'border-white/15',
                  )}
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition hover:bg-[#0d2b42]"
                    onClick={() => {
                      if (isSelected) {
                        selectCut(null);
                        return;
                      }
                      onSelectNotch?.(null);
                      selectCut(cut.id);
                    }}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold">
                        {cut.label || 'Vnútorný rez'}
                      </span>
                      <span className="mt-0.5 block truncate text-xs font-medium text-white/60">
                        {getCutPreview(cut)}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'grid shrink-0 place-items-center rounded-full text-xs font-semibold transition',
                        isSelected
                          ? sectionActionButtonClass
                          : 'bg-white/10 px-2.5 py-1 text-white/65',
                      )}
                      onClick={event => {
                        if (!isSelected) return;
                        event.stopPropagation();
                        removeCut(cutIndex);
                      }}
                    >
                      {isSelected ? <X className="h-4 w-4" /> : 'Vnútorný'}
                    </span>
                  </button>
                  {isSelected && (
                    <div
                      ref={selectedDetailRef}
                      className="grid gap-3 border-t border-white/10 p-3"
                      onPointerDownCapture={() => {
                        onSelectNotch?.(null);
                        onSelectCut?.(cut.id);
                      }}
                    >
                      <div className="hidden">
                        <button
                          type="button"
                          className={sectionActionButtonClass}
                          onClick={() => removeCut(cutIndex)}
                          aria-label="Odstrániť rez"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-1 rounded-full bg-[#071d2b] p-1">
                        <button
                          type="button"
                          className="h-9 rounded-full bg-black px-3 text-sm font-semibold text-white shadow-sm"
                        >
                          Vnútorný
                        </button>
                        <button
                          type="button"
                          className="h-9 rounded-full px-3 text-sm font-semibold text-white/65 transition hover:text-white"
                          onClick={() => convertCutToNotch(cutIndex)}
                        >
                          Obrysový
                        </button>
                      </div>

                      <label className="min-w-0">
                        <span className="mb-1 block text-xs font-semibold text-white/65">
                          Názov
                        </span>
                        <input
                          value={cut.label ?? ''}
                          onChange={event => editCut(cutIndex, { label: event.target.value })}
                          className="h-10 w-full rounded-lg border border-white/15 bg-[#071d2b] px-3 text-sm font-semibold text-white outline-none focus:border-white/45"
                        />
                      </label>

                      <div className="grid gap-3">
                        <SlotOrientationControl
                          label="Smer"
                          value={cutOrientation as HoleSlotOrientation}
                          onChange={value =>
                            editCut(cutIndex, {
                              orientation: value,
                              angleDeg:
                                value === 'horizontal'
                                  ? 0
                                  : value === 'vertical'
                                    ? 90
                                    : (cut.angleDeg ?? 45),
                            })
                          }
                        />
                        <div className="hidden">
                          <CompactNumberField
                            label="Zaoblenie okrajov"
                            value={cut.cornerRadiusMm ?? maxCutRadius}
                            onChange={value =>
                              editCut(cutIndex, {
                                shape: 'rect',
                                cornerRadiusMm: clampNumber(value, 0, maxCutRadius),
                              })
                            }
                          />
                        </div>
                      </div>

                      {cutOrientation === 'custom' && (
                        <CompactNumberField
                          label="Uhol"
                          unit="°"
                          value={cut.angleDeg ?? 45}
                          onChange={value =>
                            editCut(cutIndex, {
                              orientation: 'custom',
                              angleDeg: clampNumber(value, -180, 180),
                            })
                          }
                        />
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <CompactNumberField
                          label="X"
                          value={cut.xMm ?? 0}
                          onChange={value =>
                            editCut(cutIndex, { xMm: snapPositionMm(value, config.widthMm) })
                          }
                        />
                        <CompactNumberField
                          label="Y"
                          value={cut.yMm ?? 0}
                          onChange={value =>
                            editCut(cutIndex, { yMm: snapPositionMm(value, config.lengthMm) })
                          }
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <CompactNumberField
                          label="Šírka"
                          value={cut.widthMm ?? 0}
                          onChange={value => {
                            const nextWidth = Math.max(0.5, value);
                            const nextMaxRadius = Math.min(nextWidth, cutLength) / 2;
                            editCut(cutIndex, {
                              shape: 'rect',
                              widthMm: nextWidth,
                              cornerRadiusMm: clampNumber(
                                cut.cornerRadiusMm ?? nextMaxRadius,
                                0,
                                nextMaxRadius,
                              ),
                            });
                          }}
                        />
                        <CompactNumberField
                          label="Dĺžka"
                          value={cut.lengthMm ?? cut.widthMm ?? 0}
                          onChange={value => {
                            const nextLength = Math.max(value, cutWidth);
                            const nextMaxRadius = Math.min(cutWidth, nextLength) / 2;
                            editCut(cutIndex, {
                              shape: 'rect',
                              lengthMm: nextLength,
                              cornerRadiusMm: clampNumber(
                                cut.cornerRadiusMm ?? nextMaxRadius,
                                0,
                                nextMaxRadius,
                              ),
                            });
                          }}
                        />
                      </div>

                      <CompactNumberField
                        label="Zaoblenie okrajov"
                        value={cut.cornerRadiusMm ?? maxCutRadius}
                        onChange={value =>
                          editCut(cutIndex, {
                            shape: 'rect',
                            cornerRadiusMm: clampNumber(value, 0, maxCutRadius),
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function BendLinesPanel({
  config,
  update,
  onBack,
}: {
  config: ConfiguratorState;
  update: (patch: Partial<ConfiguratorState>) => void;
  onBack: () => void;
}) {
  const bendLines = toEditableBendLines(config);
  const bendFeatureOptions = getProductInternalCuts(config).map(cut => ({
    id: cut.id,
    name: cut.label,
  }));

  const updateBends = (nextBends: BendLineOverride[]) => {
    update({ bendLines: nextBends });
  };

  const editBend = (index: number, patch: Partial<BendLineOverride>) => {
    updateBends(
      bendLines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)),
    );
  };

  const addBend = () => {
    const usedIds = new Set(bendLines.map(line => line.id));
    let nextIndex = bendLines.length + 1;
    while (usedIds.has(`bend-${nextIndex}`)) nextIndex += 1;

    updateBends([
      ...bendLines,
      {
        id: `bend-${nextIndex}`,
        label: `Ohyb ${bendLines.length + 1}`,
        orientation: 'horizontal',
        positionMm: Math.round(config.lengthMm / 2),
        positionMode: 'manual',
        angleDeg: 90,
        direction: bendLines.length % 2 === 0 ? 'up' : 'down',
      },
    ]);
  };

  const removeBend = (index: number) => {
    updateBends(bendLines.filter((_, lineIndex) => lineIndex !== index));
  };

  const editBendMode = (index: number, mode: BendPositionMode) => {
    const firstFeatureId = bendFeatureOptions[0]?.id;
    const firstTwoFeatureIds = bendFeatureOptions.slice(0, 2).map(feature => feature.id);
    let constraint: BendPositionConstraint | undefined;

    if (mode === 'featureCenter' && firstFeatureId) {
      constraint = { type: mode, featureId: firstFeatureId, anchor: 'center' };
    } else if (mode === 'betweenFeatures' && firstTwoFeatureIds.length > 0) {
      constraint = { type: mode, featureIds: firstTwoFeatureIds, anchor: 'center' };
    } else if (mode === 'partCenter') {
      constraint = { type: mode };
    }

    editBend(index, { positionMode: mode, constraint });
  };

  return (
    <div className="grid gap-4">
      <SectionPanelHeader
        title="Ohyby dielu"
        onBack={onBack}
        onAdd={addBend}
        addLabel="Pridať ohyb"
      />

      {bendLines.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-[#092031] p-5 text-sm font-medium text-white/65">
          Diel zatiaľ nemá definovanú ohybovú líniu.
        </div>
      ) : (
        <div className="grid gap-3">
          {bendLines.map((line, index) => {
            const orientation = line.orientation ?? 'horizontal';
            const axisSize = orientation === 'horizontal' ? config.lengthMm : config.widthMm;
            const positionLimit = { min: 0, max: Math.max(1, axisSize), step: 1 };

            return (
              <div
                key={line.id}
                className="grid gap-4 rounded-2xl border border-white/15 bg-[#092031] p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <label className="min-w-0 flex-1">
                    <span className="mb-1 block text-xs font-semibold text-white/55">Názov</span>
                    <input
                      value={line.label}
                      onChange={event => editBend(index, { label: event.target.value })}
                      className="h-10 w-full rounded-lg border border-white/15 bg-[#071d2b] px-3 text-sm font-semibold text-white outline-none focus:border-white/45"
                    />
                  </label>
                  <button
                    type="button"
                    className={cn(sectionActionButtonClass, 'mt-5')}
                    onClick={() => removeBend(index)}
                    aria-label="Odstrániť ohyb"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <SelectField
                  label="Orientácia línie"
                  value={orientation}
                  onChange={value =>
                    editBend(index, {
                      orientation: value as BendOrientation,
                      positionMm:
                        value === 'horizontal'
                          ? Math.min(line.positionMm ?? config.lengthMm / 2, config.lengthMm)
                          : Math.min(line.positionMm ?? config.widthMm / 2, config.widthMm),
                    })
                  }
                  items={BEND_ORIENTATION_OPTIONS}
                />

                <SelectField
                  label="Vazba polohy"
                  value={line.positionMode ?? 'manual'}
                  onChange={value => editBendMode(index, value as BendPositionMode)}
                  items={BEND_POSITION_MODE_OPTIONS}
                />

                {line.positionMode === 'featureCenter' && bendFeatureOptions.length > 0 ? (
                  <SelectField
                    label="Vystredit na vyrez"
                    value={line.constraint?.featureId ?? bendFeatureOptions[0].id}
                    onChange={value =>
                      editBend(index, {
                        positionMode: 'featureCenter',
                        constraint: {
                          ...(line.constraint ?? {}),
                          type: 'featureCenter',
                          featureId: value,
                          anchor: 'center',
                        },
                      })
                    }
                    items={bendFeatureOptions}
                  />
                ) : null}

                {line.positionMode === 'betweenFeatures' && bendFeatureOptions.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    <SelectField
                      label="Prvy vyrez"
                      value={line.constraint?.featureIds?.[0] ?? bendFeatureOptions[0].id}
                      onChange={value => {
                        const currentIds = line.constraint?.featureIds ?? [];
                        editBend(index, {
                          positionMode: 'betweenFeatures',
                          constraint: {
                            ...(line.constraint ?? {}),
                            type: 'betweenFeatures',
                            featureIds: [
                              value,
                              currentIds[1] ?? bendFeatureOptions[1]?.id ?? value,
                            ],
                            anchor: 'center',
                          },
                        });
                      }}
                      items={bendFeatureOptions}
                    />
                    <SelectField
                      label="Druhy vyrez"
                      value={
                        line.constraint?.featureIds?.[1] ??
                        bendFeatureOptions[1]?.id ??
                        bendFeatureOptions[0].id
                      }
                      onChange={value => {
                        const currentIds = line.constraint?.featureIds ?? [];
                        editBend(index, {
                          positionMode: 'betweenFeatures',
                          constraint: {
                            ...(line.constraint ?? {}),
                            type: 'betweenFeatures',
                            featureIds: [currentIds[0] ?? bendFeatureOptions[0].id, value],
                            anchor: 'center',
                          },
                        });
                      }}
                      items={bendFeatureOptions}
                    />
                  </div>
                ) : null}

                <RangeField
                  label={
                    orientation === 'horizontal'
                      ? 'Poloha od hornej hrany'
                      : 'Poloha od ľavej hrany'
                  }
                  unit="mm"
                  value={line.positionMm ?? axisSize / 2}
                  limit={positionLimit}
                  onChange={value =>
                    editBend(index, {
                      positionMm: value,
                      positionMode: 'manual',
                      constraint: undefined,
                    })
                  }
                />

                <div className="grid grid-cols-2 gap-3">
                  <NumberControl
                    label="Uhol"
                    unit="°"
                    value={line.angleDeg ?? 90}
                    onChange={value => editBend(index, { angleDeg: clampNumber(value, 0, 180) })}
                  />
                  <SelectField
                    label="Smer"
                    value={line.direction ?? 'up'}
                    onChange={value => editBend(index, { direction: value as BendDirection })}
                    items={BEND_DIRECTION_OPTIONS}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConfiguratorDebugButton({
  config,
  estimate,
  resolvedGeometry,
}: {
  config: ConfiguratorState;
  estimate: ReturnType<typeof calculateResolvedEstimate>;
  resolvedGeometry: ReturnType<typeof resolvePartGeometry>;
}) {
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  if (!SHOW_CONFIGURATOR_DEBUG) return null;

  const copyDebugState = async () => {
    const selected = getSelectedOptions(config);
    const bendMeshDebug = await createBendMeshDebugReport(config).catch(error => ({
      error: error instanceof Error ? error.message : String(error),
    }));
    const payload = {
      generatedAt: new Date().toISOString(),
      selectedOptions: {
        product: selected.product,
        machine: selected.machine,
        finish: selected.finish,
        material: selected.material,
        holePattern: selected.holePattern,
      },
      config,
      derived: {
        estimate,
        holes: getHoleLayout(config),
        internalCuts: getProductInternalCuts(config),
        outlineNotches: getProductOutlineNotches(config),
        outlineSettings: getProductOutlineSettings(config),
        bendLines: getProductBendLines(config),
        warnings: resolvedGeometry.warnings,
        boundingBox: resolvedGeometry.boundingBox,
        areaMm2: resolvedGeometry.areaMm2,
        cutLengthMm: resolvedGeometry.cutLengthMm,
        finalOutlinePath: resolvedGeometry.finalOutlinePath,
        openingPaths: resolvedGeometry.openingPaths,
        cutPaths: resolvedGeometry.cutPaths,
        exportEntities: resolvedGeometry.exportEntities,
        bendMeshDebug,
      },
    };
    const text = JSON.stringify(payload, null, 2);

    console.log('Lisonex configurator debug state', payload);

    try {
      await navigator.clipboard.writeText(text);
      setStatus('copied');
      window.setTimeout(() => setStatus('idle'), 2200);
    } catch {
      setStatus('failed');
      window.setTimeout(() => setStatus('idle'), 3200);
    }
  };

  return (
    <button
      type="button"
      onClick={copyDebugState}
      className="flex h-9 items-center justify-center border border-white/15 bg-white/5 px-3 text-xs font-semibold text-white/75 transition hover:border-[#e87500]/70 hover:bg-[#0b2a42] hover:text-white"
    >
      {status === 'copied'
        ? 'Debug stav skopírovaný'
        : status === 'failed'
          ? 'Debug je v konzole'
          : 'Kopírovať debug stav'}
    </button>
  );
}

function ParametersStep({
  config,
  estimate,
  update,
  updatePlateSize,
  viewMode,
  setViewMode,
  selectedHoleIndexes,
  activeHoleIndex,
  onHoleSelectionChange,
  holeGridStepMm,
  setHoleGridStepMm,
}: {
  config: ConfiguratorState;
  estimate: ReturnType<typeof calculateResolvedEstimate>;
  update: (patch: Partial<ConfiguratorState>) => void;
  updatePlateSize: (patch: Partial<Pick<ConfiguratorState, 'widthMm' | 'lengthMm'>>) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  selectedHoleIndexes: number[];
  activeHoleIndex: number | null;
  onHoleSelectionChange: (indexes: number[], activeIndex: number | null) => void;
  holeGridStepMm: number;
  setHoleGridStepMm: (value: number) => void;
}) {
  const selected = getSelectedOptions(config);
  const productData = getConfiguratorDataForProduct(config.productTypeId);
  const resolvedGeometry = useMemo(() => resolvePartGeometry(config), [config]);
  const limits = useMemo(() => getEffectiveLimits(config), [config]);
  const previewSummaryRows: Array<[string, string]> = [
    ['Šírka (mm)', formatCompactNumber(config.widthMm)],
    ['Dĺžka (mm)', formatCompactNumber(config.lengthMm)],
    ['Hrúbka (mm)', formatCompactNumber(config.thicknessMm)],
    ['Povrchová úprava', selected.finish.name],
    ['Typ materiálu', selected.material.name],
    ['Množstvo', formatCompactNumber(config.quantity, 0)],
    ['Dĺžka rezu', `~ ${formatNumber(resolvedGeometry.cutLengthMm / 1000, 2)} m`],
    ['Termín', formatLeadTime(estimate.leadTimeDays)],
  ];
  const previewQuickRows: Array<[string, string]> = [
    ['Plocha', `${formatNumber(estimate.areaM2, 3)} m²`],
    ['Hmotnosť', `~ ${formatWeight(estimate.weightKg)}/ks`],
    ['Dĺžka rezu', `~ ${formatNumber(resolvedGeometry.cutLengthMm / 1000, 2)} m`],
    ['Termín', formatLeadTime(estimate.leadTimeDays)],
  ];
  const hasSelectedHoles = selectedHoleIndexes.some(
    index => index >= 0 && index < config.holeCount,
  );
  const [sidebarPanel, setSidebarPanel] = useState<
    'product' | 'corners' | 'shape' | 'cuts' | 'bends'
  >('product');
  const [previewDetailsOpen, setPreviewDetailsOpen] = useState(false);
  const [selectedInternalCutId, setSelectedInternalCutId] = useState<string | null>(null);
  const [selectedOutlineNotchId, setSelectedOutlineNotchId] = useState<string | null>(null);
  const supportsCornerTreatment = productSupportsFeature(config, 'corner-treatment');
  const isCornerPanel = !hasSelectedHoles && sidebarPanel === 'corners' && supportsCornerTreatment;
  const isShapePanel = !hasSelectedHoles && sidebarPanel === 'shape';
  const isCutsPanel = !hasSelectedHoles && sidebarPanel === 'cuts';
  const isBendsPanel = !hasSelectedHoles && sidebarPanel === 'bends';
  const isSectionPanel = isCornerPanel || isShapePanel || isCutsPanel || isBendsPanel;
  const handleHoleSelectionChange = (indexes: number[], activeIndex: number | null) => {
    if (indexes.length) setSelectedInternalCutId(null);
    onHoleSelectionChange(indexes, activeIndex);
  };
  const handleInternalCutSelectionChange = (id: string | null) => {
    setSelectedInternalCutId(id);
    if (id) setSelectedOutlineNotchId(null);
    if (!id) return;

    onHoleSelectionChange([], null);
    setSidebarPanel('cuts');
  };
  const handleOutlineNotchSelectionChange = (id: string | null) => {
    setSelectedOutlineNotchId(id);
    if (id) setSelectedInternalCutId(null);
    if (!id) return;

    onHoleSelectionChange([], null);
    setSidebarPanel('cuts');
  };
  const openSidebarSection = (panel: typeof sidebarPanel) => {
    onHoleSelectionChange([], null);
    setSelectedInternalCutId(null);
    setSelectedOutlineNotchId(null);
    setSidebarPanel(panel);
  };
  const sectionShortcuts = [
    {
      id: 'product' as const,
      label: 'Parametre',
      title: 'Parametre výrobku',
      summary: 'Rozmery, materiál, výroba',
      icon: SlidersHorizontal,
    },
    {
      id: 'cuts' as const,
      label: 'Rezy',
      title: 'Rezy / výrezy',
      summary: getInternalCutsSummary(config),
      icon: CutsSectionIcon,
    },
    {
      id: 'bends' as const,
      label: 'Ohyby',
      title: 'Ohyby dielu',
      summary: getBendLinesSummary(config),
      icon: BendsSectionIcon,
    },
    ...(supportsCornerTreatment
      ? [
          {
            id: 'corners' as const,
            label: 'Rohy',
            title: 'Rohy dielu',
            summary: getCornerSummary(config),
            icon: CornersSectionIcon,
          },
        ]
      : []),
  ];
  return (
    <div
      className="mx-auto grid w-full max-w-[1660px] items-start gap-6 px-0 py-0 2xl:grid-cols-[minmax(0,1fr)_430px] 2xl:gap-7 2xl:px-0 min-[1800px]:grid-cols-[minmax(0,1fr)_460px]"
      data-configurator-parameters-layout
    >
      <div data-configurator-preview-sticky>
        <Panel
          className="relative w-full overflow-hidden 2xl:flex 2xl:h-full 2xl:flex-col"
          data-configurator-preview-panel
        >
          <div className="shrink-0 bg-[#15354d] px-2 pt-2">
            <div className="grid grid-cols-2 gap-2 bg-[#082033] p-1">
              {(['2d', '3d'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={cn(
                    'h-10 border px-4 text-sm font-semibold transition',
                    viewMode === mode
                      ? 'border-white bg-white text-[#071d2b]'
                      : 'border-transparent bg-[#082033] text-white/55 hover:text-white',
                  )}
                >
                  {mode.toUpperCase()} pohľad
                </button>
              ))}
            </div>
          </div>
          <div className="h-4 shrink-0 bg-[#15354d]" aria-hidden="true" />
          <div data-configurator-preview-canvas className="min-h-[520px] 2xl:min-h-0 2xl:flex-1">
            <PlatePreview
              config={config}
              viewMode={viewMode}
              update={update}
              selectedHoleIndexes={selectedHoleIndexes}
              activeHoleIndex={activeHoleIndex}
              onHoleSelectionChange={handleHoleSelectionChange}
              selectedInternalCutId={selectedInternalCutId}
              onInternalCutSelectionChange={handleInternalCutSelectionChange}
              selectedOutlineNotchId={selectedOutlineNotchId}
              onOutlineNotchSelectionChange={handleOutlineNotchSelectionChange}
              snapStepMm={holeGridStepMm}
              showBendGuides={isBendsPanel}
            />
          </div>
          <div
            data-configurator-preview-summary
            className={cn(
              'relative flex cursor-pointer flex-col overflow-hidden border-t border-white/10 bg-[#15354d] shadow-[0_-18px_40px_rgba(0,0,0,0.24)] 2xl:absolute 2xl:inset-x-0 2xl:bottom-0 2xl:z-30 2xl:transition-[max-height] 2xl:duration-200 2xl:ease-out',
              previewDetailsOpen ? '2xl:max-h-[520px]' : '2xl:max-h-20',
            )}
            role="button"
            tabIndex={0}
            aria-expanded={previewDetailsOpen}
            onClick={() => setPreviewDetailsOpen(open => !open)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setPreviewDetailsOpen(open => !open);
              }
            }}
          >
            <div className="flex h-20 shrink-0 items-center px-4 py-2 2xl:px-5">
              <div className="grid min-w-0 flex-1 grid-cols-4 gap-2">
                {previewQuickRows.map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <p className="truncate text-[10px] leading-tight text-white/55">{label}</p>
                    <strong
                      className="mt-1 block truncate text-[13px] leading-tight text-white"
                      title={value}
                    >
                      {value}
                    </strong>
                  </div>
                ))}
              </div>

              <div className="ml-2 flex shrink-0 items-center">
                <button
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    setPreviewDetailsOpen(open => !open);
                  }}
                  aria-expanded={previewDetailsOpen}
                  className="grid h-9 w-9 place-items-center text-white transition hover:text-[#e87500]"
                  title={previewDetailsOpen ? 'Skryť detaily' : 'Zobraziť detaily'}
                >
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 rotate-180 transition-transform',
                      previewDetailsOpen && 'rotate-0',
                    )}
                  />
                </button>
              </div>
            </div>

            {previewDetailsOpen && (
              <div className="border-t border-white/10 px-4 py-3 2xl:px-5">
                <dl className="space-y-1.5">
                  {previewSummaryRows.map(([label, value]) => (
                    <div key={label} className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 text-sm">
                      <dt className="min-w-0 text-white/55">{label}</dt>
                      <dd className="max-w-[190px] text-right font-semibold leading-snug text-white">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </Panel>
      </div>

      <div data-configurator-mobile-section-nav aria-label="Sekcie parametrov">
        {sectionShortcuts.map(item => {
          const Icon = item.icon;
          const active = !hasSelectedHoles && sidebarPanel === item.id;

          return (
            <button
              key={item.id}
              type="button"
              data-configurator-sidebar-shortcut
              data-active={active ? 'true' : undefined}
              className={cn(
                'group flex min-h-[56px] items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition',
                active
                  ? 'border-[#e87500] bg-[#e87500] text-white shadow-[0_12px_26px_rgba(232,117,0,0.18)]'
                  : 'border-white/15 bg-[#092031] text-white hover:border-[#e87500]/70 hover:bg-[#0b2a42]',
              )}
              onClick={() => openSidebarSection(item.id)}
              aria-pressed={active}
              title={item.title}
            >
              <span
                className={cn(
                  'grid h-9 w-9 shrink-0 place-items-center rounded-lg border transition',
                  active
                    ? 'bg-white/16 border-white/30'
                    : 'border-white/12 bg-white/5 text-[#e87500] group-hover:border-[#e87500]/55',
                )}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <strong className="block text-sm font-semibold leading-tight !text-white">
                  {item.label}
                </strong>
                <span className="mt-0.5 block truncate text-[11px] leading-tight text-white/60">
                  {item.summary}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <aside data-configurator-sidebar data-configurator-sidebar-scroll>
        <Panel className="p-5 lg:p-6">
          <div className="mb-5 grid gap-3">
            <div
              data-configurator-sidebar-section-nav
              className="grid grid-cols-2 gap-2"
              aria-label="Sekcie parametrov"
            >
              {sectionShortcuts.map(item => {
                const Icon = item.icon;
                const active = !hasSelectedHoles && sidebarPanel === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    data-configurator-sidebar-shortcut
                    data-active={active ? 'true' : undefined}
                    className={cn(
                      'group flex min-h-[56px] items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition',
                      active
                        ? 'border-[#e87500] bg-[#e87500] text-white shadow-[0_12px_26px_rgba(232,117,0,0.18)]'
                        : 'border-white/15 bg-[#092031] text-white hover:border-[#e87500]/70 hover:bg-[#0b2a42]',
                    )}
                    onClick={() => openSidebarSection(item.id)}
                    aria-pressed={active}
                    title={item.title}
                  >
                    <span
                      className={cn(
                        'grid h-9 w-9 shrink-0 place-items-center rounded-lg border transition',
                        active
                          ? 'bg-white/16 border-white/30'
                          : 'border-white/12 bg-white/5 text-[#e87500] group-hover:border-[#e87500]/55',
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <strong className="block text-sm font-semibold leading-tight !text-white">
                        {item.label}
                      </strong>
                      <span className="mt-0.5 block truncate text-[11px] leading-tight text-white/60">
                        {item.summary}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <ConfiguratorDebugButton
              config={config}
              estimate={estimate}
              resolvedGeometry={resolvedGeometry}
            />
          </div>
          {!isSectionPanel && (
            <>
              <h2 className="text-xl font-semibold tracking-normal lg:text-2xl">
                {hasSelectedHoles ? 'Parametre otvoru' : 'Parametre výrobku'}
              </h2>
              <div className="my-5 h-px bg-white/15" />
            </>
          )}
          {hasSelectedHoles ? (
            <HoleSelectionPanel
              config={config}
              update={update}
              selectedHoleIndexes={selectedHoleIndexes}
              activeHoleIndex={activeHoleIndex}
              onHoleSelectionChange={handleHoleSelectionChange}
              gridStepMm={holeGridStepMm}
              setGridStepMm={setHoleGridStepMm}
            />
          ) : isCornerPanel ? (
            <CornerSelectionPanel
              config={config}
              update={update}
              limits={limits}
              onBack={() => setSidebarPanel('product')}
            />
          ) : isShapePanel ? (
            <ShapeSelectionPanel
              config={config}
              update={update}
              onBack={() => setSidebarPanel('product')}
            />
          ) : isCutsPanel ? (
            <InternalCutsPanel
              config={config}
              update={update}
              onBack={() => setSidebarPanel('product')}
              selectedCutId={selectedInternalCutId}
              onSelectCut={handleInternalCutSelectionChange}
              selectedNotchId={selectedOutlineNotchId}
              onSelectNotch={handleOutlineNotchSelectionChange}
              gridStepMm={holeGridStepMm}
            />
          ) : isBendsPanel ? (
            <BendLinesPanel
              config={config}
              update={update}
              onBack={() => setSidebarPanel('product')}
            />
          ) : (
            <div className="grid gap-5">
              <RangeField
                label="Šírka"
                unit="mm"
                value={config.widthMm}
                limit={limits.widthMm}
                onChange={value => updatePlateSize({ widthMm: value })}
              />
              <RangeField
                label="Dĺžka"
                unit="mm"
                value={config.lengthMm}
                limit={limits.lengthMm}
                onChange={value => updatePlateSize({ lengthMm: value })}
              />
              <RangeField
                label="Hrúbka"
                unit="mm"
                value={config.thicknessMm}
                limit={limits.thicknessMm}
                onChange={value => update({ thicknessMm: value })}
              />
              {!supportsCornerTreatment && (
                <RangeField
                  label={'Zaoblenie hrany'}
                  unit="mm"
                  value={config.edgeRadiusMm}
                  limit={limits.edgeRadiusMm}
                  onChange={value => update({ edgeRadiusMm: value })}
                />
              )}

              <div>
                <label className="text-base font-semibold">Povrchová úprava</label>
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {productData.finishes.map(finish => (
                    <button
                      key={finish.id}
                      onClick={() => update({ finishId: finish.id })}
                      className="grid justify-items-center gap-2 text-xs font-medium text-white/65 transition hover:text-white"
                    >
                      <span
                        className="flex h-9 w-9 items-center justify-center rounded-full"
                        style={{ background: finish.color }}
                      >
                        {finish.id === config.finishId && <Check className="h-5 w-5 text-white" />}
                      </span>
                      {finish.name}
                    </button>
                  ))}
                </div>
              </div>

              <SelectField
                label="Typ materiálu"
                value={config.materialId}
                onChange={value => update({ materialId: value })}
                items={productData.materials}
              />
              <StepperInput
                label="Množstvo"
                value={config.quantity}
                onChange={value => update({ quantity: clampToLimit('quantity', value, config) })}
              />
            </div>
          )}
        </Panel>
      </aside>
    </div>
  );
}

function getDirectPartExportConfig(config: ConfiguratorState): ConfiguratorState {
  const marginMm = 10;

  return {
    ...config,
    quantity: 1,
    nestingGapMm: 0,
    sheetPresetId: 'custom',
    sheetWidthMm: Math.max(config.widthMm + marginMm * 2, config.widthMm),
    sheetLengthMm: Math.max(config.lengthMm + marginMm * 2, config.lengthMm),
  };
}

function SummaryDownloadsPanel({ config }: { config: ConfiguratorState }) {
  const directConfig = getDirectPartExportConfig(config);

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-[#092031] p-5 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
      <h3 className="text-lg font-semibold tracking-normal">Na stiahnutie</h3>

      <div className="mt-4 grid gap-3">
        <Button
          className="h-12 w-full justify-center gap-3 rounded-none bg-white px-5 text-[#071d2b] hover:bg-[#f2f2f2]"
          onClick={() => openTechnicalDrawingDocument(config, calculateResolvedEstimate(config))}
        >
          <Download className="h-4 w-4" />
          Stiahnuť výrobný výkres
        </Button>
        <Button
          className="h-12 w-full justify-center gap-3 rounded-none bg-white px-5 text-[#071d2b] hover:bg-[#f2f2f2]"
          onClick={() => openDxfSheetDocument(directConfig, 0)}
        >
          <Download className="h-4 w-4" />
          Stiahnuť DXF dielu
        </Button>
      </div>
    </div>
  );
}

function SummaryStep({
  config,
  estimate,
  isSubmitted = false,
  onConfigurationNameChange,
  onEdit,
  quoteCustomerData,
  onQuoteCustomerDataChange,
}: {
  config: ConfiguratorState;
  estimate: ReturnType<typeof calculateResolvedEstimate>;
  isSubmitted?: boolean;
  onConfigurationNameChange: (value: string) => void;
  onEdit: () => void;
  quoteCustomerData: QuoteCustomerData;
  onQuoteCustomerDataChange: (patch: Partial<QuoteCustomerData>) => void;
}) {
  const { t } = useTranslations();
  const selected = getSelectedOptions(config);
  const isCompanyPurchase = quoteCustomerData.purchaseType === 'company';
  const updateQuoteCustomerData = (patch: Partial<QuoteCustomerData>) =>
    onQuoteCustomerDataChange(patch);
  const bendLines = getProductBendLines(config);
  const summaryRows = [
    ['Typ výrobku', selected.product.name],
    ['Typ stroja', selected.machine.name],
    ['Šírka', `${config.widthMm} mm`],
    ['Dĺžka', `${config.lengthMm} mm`],
    ['Hrúbka', `${config.thicknessMm} mm`],
    ...(bendLines.length ? [['Ohyby', getBendLinesSummary(config)]] : []),
    ['Povrchová úprava', selected.finish.name],
    [t('text4065'), selected.material.name],
    ['Dĺžka rezu', `~ ${formatNumber(estimate.cutLengthM, 2)} m`],
    ['Termín', formatLeadTime(estimate.leadTimeDays)],
  ];

  return (
    <div
      className="mx-auto grid max-w-[1664px] gap-8 px-5 py-10 sm:px-8 lg:px-10 lg:py-16 min-[1500px]:grid-cols-2"
      data-configurator-summary
    >
      <Panel className="p-6 [container-type:inline-size] lg:p-8">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold tracking-normal">{t('text4089')}</h2>
            <p className="mt-2 text-base text-[#777]">
              {isSubmitted
                ? 'Skontrolujte parametre pred uložením zmien'
                : 'Skontrolujte parametre pred odoslaním'}
            </p>
          </div>
          <button className="flex items-center gap-2 font-semibold text-[#e87500]" onClick={onEdit}>
            Upraviť <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <label className="mb-6 block">
          <span className="mb-2 block text-sm font-semibold text-white/70">
            Názov konfigurácie a súborov
          </span>
          <input
            data-configurator-name-input
            type="text"
            value={config.configurationName ?? selected.product.name}
            maxLength={80}
            onChange={event => onConfigurationNameChange(event.target.value)}
            className="h-14 w-full border border-white/15 bg-[#092031] px-4 text-base text-white outline-none transition placeholder:text-white/45 focus:border-white/45"
            aria-describedby="configuration-name-help"
          />
          <span id="configuration-name-help" className="mt-2 block text-sm text-white/50">
            Použije sa v prehľade aj ako prefix PDF, DXF a NEX súborov.
          </span>
        </label>
        <div
          className="mx-auto mb-6 w-full max-w-[620px] overflow-hidden rounded-2xl"
          style={{ height: 'clamp(260px, 58cqw, 390px)' }}
        >
          <ProductTypePreview3D config={config} />
        </div>
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-2xl font-semibold">{selected.product.name}</h3>
          <span className="rounded-full border border-[#d9d9d9] px-4 py-2 text-sm">
            Množstvo: {config.quantity} ks
          </span>
        </div>
        <div className="h-px bg-[#d9d9d9]" />
        <dl className="my-6 grid gap-3 text-lg">
          {summaryRows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4">
              <dt className="text-[#777]">{label}</dt>
              <dd className="text-right font-semibold">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="grid grid-cols-3 gap-4 border-t border-[#d9d9d9] pt-6">
          <SummaryTile label="Plocha" value={`${formatNumber(estimate.areaM2, 3)} m²`} />
          <SummaryTile label="Hmotnosť" value={`~ ${formatWeight(estimate.weightKg)}/ks`} />
          <SummaryTile label="Spolu" value={`~ ${formatWeight(estimate.totalWeightKg)}`} />
        </div>
        <SummaryDownloadsPanel config={config} />
      </Panel>

      {isSubmitted ? (
        <SubmittedConfigurationNotice />
      ) : (
        <QuoteContactPanel
          quoteCustomerData={quoteCustomerData}
          isCompanyPurchase={isCompanyPurchase}
          onChange={updateQuoteCustomerData}
        />
      )}
    </div>
  );
}

function SubmittedConfigurationNotice() {
  return (
    <Panel
      className="flex min-h-[360px] flex-col justify-center p-6 lg:p-8"
      data-configurator-submitted-notice
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#2f9560] text-white">
        <Check className="h-7 w-7" />
      </div>
      <h2 className="mt-6 text-2xl font-semibold tracking-normal">Konfigurácia už bola odoslaná</h2>
      <p className="mt-3 max-w-[560px] text-base leading-relaxed text-white/65">
        Kontaktné a fakturačné údaje boli odoslané pri pôvodnom odoslaní, preto ich pri úprave
        nemusíte vypĺňať znova.
      </p>
      <div className="mt-7 border border-white/10 bg-[#092031] p-5 text-sm leading-relaxed text-white/70">
        Uložením zmien aktualizujete tú istú konfiguráciu. Jej stav odoslania zostane zachovaný.
      </div>
    </Panel>
  );
}

function QuoteContactPanel({
  quoteCustomerData,
  isCompanyPurchase,
  onChange,
}: {
  quoteCustomerData: QuoteCustomerData;
  isCompanyPurchase: boolean;
  onChange: (patch: Partial<QuoteCustomerData>) => void;
}) {
  const { t } = useTranslations();
  return (
    <Panel className="p-6 lg:p-8">
      <h2 className="text-2xl font-semibold tracking-normal">Vaše údaje ku konfigurácii</h2>
      <p className="mt-2 text-base text-white/60">
        Tieto údaje použijeme len na spracovanie konfigurácie a kontakt.
      </p>
      <div className="mt-7 flex flex-wrap gap-3">
        <button
          type="button"
          className={cn(
            'px-5 py-4 font-semibold transition',
            !isCompanyPurchase
              ? 'bg-white text-[#071d2b]'
              : 'bg-[#082033] text-white/60 hover:text-white',
          )}
          onClick={() => onChange({ purchaseType: 'person' })}
        >
          {!isCompanyPurchase ? (
            <span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#e87500]" />
          ) : null}
          {t('text1113')}
        </button>
        <button
          type="button"
          className={cn(
            'px-5 py-4 font-semibold transition',
            isCompanyPurchase
              ? 'bg-white text-[#071d2b]'
              : 'bg-[#082033] text-white/60 hover:text-white',
          )}
          onClick={() => onChange({ purchaseType: 'company' })}
        >
          {isCompanyPurchase ? (
            <span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#e87500]" />
          ) : null}
          Nákup na firmu
        </button>
      </div>

      {isCompanyPurchase ? (
        <FormBlock title="Firma">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <InputLike
              placeholder="IČO *"
              value={quoteCustomerData.companyIco}
              onChange={value => onChange({ companyIco: value })}
            />
            <Button
              type="button"
              variant="outline"
              className="h-16 rounded-none border-white/15 bg-white/10 px-7 text-white hover:bg-white/15"
            >
              {t('text4101')}
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <InputLike
              placeholder="Názov firmy *"
              value={quoteCustomerData.companyName}
              onChange={value => onChange({ companyName: value })}
            />
            <InputLike
              placeholder="DIČ"
              value={quoteCustomerData.companyDic}
              onChange={value => onChange({ companyDic: value })}
            />
          </div>
          <InputLike
            placeholder="IČ DPH"
            value={quoteCustomerData.companyIcDph}
            onChange={value => onChange({ companyIcDph: value })}
          />
          <InputLike
            placeholder="Sídlo / fakturačná adresa *"
            value={quoteCustomerData.billingAddress}
            onChange={value => onChange({ billingAddress: value })}
          />
        </FormBlock>
      ) : (
        <FormBlock title="Fakturačné údaje">
          <InputLike
            placeholder="Fakturačná adresa"
            value={quoteCustomerData.billingAddress}
            onChange={value => onChange({ billingAddress: value })}
          />
        </FormBlock>
      )}

      <FormBlock title="Kontaktná osoba">
        <InputLike
          placeholder="Meno a priezvisko *"
          value={quoteCustomerData.contactName}
          onChange={value => onChange({ contactName: value })}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <InputLike
            placeholder="E-mail *"
            value={quoteCustomerData.email}
            onChange={value => onChange({ email: value })}
            type="email"
          />
          <InputLike
            placeholder="Telefón *"
            value={quoteCustomerData.phone}
            onChange={value => onChange({ phone: value })}
            type="tel"
          />
        </div>
      </FormBlock>

      <FormBlock title="Doplňujúce informácie">
        <div className="grid gap-4 sm:grid-cols-2">
          <DateInputLike
            placeholder="Termín dodania"
            value={quoteCustomerData.deliveryDate}
            onChange={value => onChange({ deliveryDate: value })}
          />
          <InputLike
            placeholder="Spôsob dodania"
            value={quoteCustomerData.deliveryMethod}
            onChange={value => onChange({ deliveryMethod: value })}
          />
        </div>
        <TextareaLike
          placeholder={t('text4094')}
          value={quoteCustomerData.note}
          onChange={value => onChange({ note: value })}
        />
      </FormBlock>

      <p className="mt-6 text-base leading-snug">
        Odoslaním súhlasíte so{' '}
        <span className="font-semibold text-[#e87500] underline">spracovaním osobných údajov</span>{' '}
        pre účely spracovania konfigurácie v zmysle GDPR.
      </p>
    </Panel>
  );
}

function SuccessScreen({
  config,
  createdConfiguration,
  estimate,
  isEditing,
  onNew,
}: {
  config: ConfiguratorState;
  createdConfiguration?: StoredConfiguratorConfiguration | null;
  estimate: ReturnType<typeof calculateResolvedEstimate>;
  isEditing?: boolean;
  onNew: () => void;
}) {
  const { t } = useTranslations();
  const configurationCode = createdConfiguration?.token
    ? `#${createdConfiguration.token.slice(0, 8).toUpperCase()}`
    : SUCCESS_CONTENT.configurationCode;
  const directExportConfig = getDirectPartExportConfig(config);

  return (
    <div
      className="mx-auto flex min-h-full w-full max-w-[900px] items-center px-5 py-4 sm:px-8 lg:py-5"
      data-configurator-success
    >
      <Panel className="w-full p-5 text-center sm:p-7 lg:p-8">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#2f9560] text-white">
          <Check className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-[32px] font-semibold leading-tight sm:text-[44px]">
          {isEditing ? 'Konfigurácia bola uložená' : t(SUCCESS_CONTENT.title)}
        </h1>
        <p className="mx-auto mt-3 max-w-[720px] text-base leading-snug text-white/70">
          {isEditing
            ? 'Zmeny v konfigurácii sú uložené. K výstupom sa môžete vrátiť cez detail konfigurácie.'
            : SUCCESS_CONTENT.subtitle}
        </p>
        <div className="mt-5 flex flex-col gap-3 bg-[#071d2b] p-4 text-left sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-white/50">Kód konfigurácie</p>
            <strong className="text-xl">{configurationCode}</strong>
          </div>
          <div className="sm:text-right">
            <p className="text-white/50">{t('text4103')}</p>
            <strong className="text-sm sm:text-base">{SUCCESS_CONTENT.sentAt}</strong>
          </div>
        </div>
        <div className="mt-5 text-left">
          <p className="mb-3 text-base text-white/70">Čo bude nasledovať</p>
          <div className="grid gap-3">
            {SUCCESS_CONTENT.nextSteps.map((step, index) => (
              <div key={step.title} className="grid grid-cols-[40px_1fr] gap-4 bg-[#0a2438] p-4">
                <span className="flex h-9 w-9 items-center justify-center border border-white/20 text-sm font-semibold text-white">
                  {index + 1}
                </span>
                <span>
                  <strong className="block text-base">{step.title}</strong>
                  <span className="mt-1 block text-sm leading-snug text-white/55">
                    {step.description}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button
            variant="outline"
            className="h-12 rounded-none border-white/20 bg-white/5 px-5 text-white hover:bg-white/10"
            onClick={onNew}
          >
            {t('text4066')}
          </Button>
          <Button
            className="h-12 rounded-none bg-[#e87500] px-5 text-white hover:bg-[#cf6800]"
            onClick={() => openTechnicalDrawingDocument(config, estimate)}
          >
            <Download className="h-5 w-5" />
            Stiahnuť výrobný výkres
          </Button>
          <Button
            className="h-12 rounded-none bg-[#e87500] px-5 text-white hover:bg-[#cf6800]"
            onClick={() => openDxfSheetDocument(directExportConfig, 0)}
          >
            <Download className="h-5 w-5" />
            Stiahnuť DXF
          </Button>
        </div>
      </Panel>
    </div>
  );
}

function StepShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-configurator-step-shell
      className="mx-auto flex min-h-full w-full max-w-[1500px] flex-col justify-start px-0 py-0"
    >
      <div className="mx-auto mb-8 max-w-[760px] text-center lg:hidden">
        <h1 className="text-[38px] font-semibold leading-[1.02] tracking-normal text-white sm:text-[50px] lg:text-[58px]">
          {title}
        </h1>
        <p className="mx-auto mt-4 max-w-[560px] text-base leading-snug text-white/65 lg:text-lg">
          {subtitle}
        </p>
      </div>
      {children}
    </div>
  );
}

function getDraftInputWidthCh(value: string, unit = '') {
  return Math.min(24, Math.max(10, value.length + unit.length + 5));
}

function RangeField({
  label,
  unit,
  value,
  limit,
  onChange,
}: {
  label: string;
  unit: string;
  value: number;
  limit: { min: number; max: number; step: number };
  onChange: (value: number) => void;
}) {
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const commit = (nextValue: number) => {
    if (Number.isNaN(nextValue)) return;
    onChange(Math.min(Math.max(nextValue, limit.min), limit.max));
  };

  const commitDraft = () => {
    const parsed = Number(draftValue.replace(',', '.'));
    if (Number.isFinite(parsed)) {
      commit(parsed);
    } else {
      setDraftValue(String(value));
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-4">
        <label className="text-base font-semibold">{label}</label>
        <div
          className="flex h-9 min-w-[128px] items-center border border-white/15 bg-[#092031] pr-3"
          style={{ width: `${getDraftInputWidthCh(draftValue, unit)}ch` }}
        >
          <input
            type="text"
            inputMode="decimal"
            value={draftValue}
            onBlur={commitDraft}
            onChange={event => setDraftValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
            }}
            className="h-full min-w-0 flex-1 rounded-md bg-transparent px-3 text-right text-base outline-none"
          />
          <span className="text-sm text-white/55">{unit}</span>
        </div>
      </div>
      <input
        type="range"
        min={limit.min}
        max={limit.max}
        step={limit.step}
        value={value}
        onChange={event => commit(Number(event.target.value))}
        className="h-2 w-full accent-[#e87500]"
      />
      <div className="mt-1 flex justify-between text-xs text-white/45">
        <span>Min {limit.min}</span>
        <span>Max {limit.max}</span>
      </div>
    </div>
  );
}

function StepperInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const commitDraft = () => {
    const parsed = Number(draftValue.replace(',', '.'));
    if (Number.isFinite(parsed)) {
      onChange(parsed);
    } else {
      setDraftValue(String(value));
    }
  };

  return (
    <div>
      <label className="mb-2 block text-base font-semibold">{label}</label>
      <div className="grid h-12 grid-cols-[48px_1fr_48px] items-center border border-white/15 bg-[#092031] p-1.5">
        <button
          className="flex h-9 items-center justify-center bg-white/10 text-[#e87500]"
          onClick={() => onChange(value - 1)}
        >
          <Minus className="h-5 w-5" />
        </button>
        <input
          type="text"
          inputMode="decimal"
          value={draftValue}
          onBlur={commitDraft}
          onChange={event => setDraftValue(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
          className="h-full min-w-0 bg-transparent text-center text-base outline-none"
        />
        <button
          className="flex h-9 items-center justify-center bg-white/10 text-[#e87500]"
          onClick={() => onChange(value + 1)}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  items,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  items: Array<{ id: string; name: string }>;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-white/55">{label}</span>
      <span className="relative block">
        <select
          value={value}
          onChange={event => onChange(event.target.value)}
          className="h-10 w-full appearance-none border border-white/15 bg-[#092031] px-3 pr-9 text-sm font-semibold text-white outline-none focus:border-white/45"
        >
          {items.map(item => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#e87500]" />
      </span>
    </label>
  );
}

function CompactNumberField({
  label,
  value,
  unit = 'mm',
  onChange,
}: {
  label: string;
  value: number;
  unit?: string;
  onChange: (value: number) => void;
}) {
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const commitDraft = () => {
    const parsed = Number(draftValue.replace(',', '.'));
    if (Number.isFinite(parsed)) {
      onChange(parsed);
    } else {
      setDraftValue(String(value));
    }
  };

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-white/55">{label}</span>
      <span
        className="flex h-10 min-w-[150px] items-center border border-white/15 bg-[#092031] pr-3 focus-within:border-white/45"
        style={{ width: `${getDraftInputWidthCh(draftValue, unit)}ch` }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={draftValue}
          onBlur={commitDraft}
          onChange={event => setDraftValue(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
          className="h-full min-w-0 flex-1 rounded-lg bg-transparent px-3 text-sm font-semibold outline-none"
        />
        <span className="text-xs font-semibold text-white/55">{unit}</span>
      </span>
    </label>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[#092031] p-4">
      <p className="text-white/55">{label}</p>
      <strong className="mt-1 block text-lg">{value}</strong>
    </div>
  );
}

function FormBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-8">
      <h3 className="mb-4 text-2xl font-semibold tracking-normal">{title}</h3>
      <div className="grid gap-4">{children}</div>
    </div>
  );
}

function InputLike({
  placeholder,
  value = '',
  onChange,
  type = 'text',
}: {
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
  type?: string;
}) {
  return (
    <input
      data-configurator-quote-input
      type={type}
      aria-label={placeholder}
      placeholder={placeholder}
      value={value}
      readOnly={!onChange}
      onChange={event => onChange?.(event.target.value)}
      className="h-16 border border-white/15 bg-[#092031] px-5 text-lg text-white outline-none transition placeholder:text-white/45 focus:border-white/45"
    />
  );
}

function isDateInputValue(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function DateInputLike({
  placeholder,
  value = '',
  onChange,
}: {
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dateValue = isDateInputValue(value) ? value : '';

  return (
    <div className="relative">
      <input
        ref={inputRef}
        data-configurator-quote-input
        data-configurator-date-input
        data-empty={dateValue ? undefined : 'true'}
        type="date"
        aria-label={placeholder}
        value={dateValue}
        readOnly={!onChange}
        onChange={event => onChange?.(event.target.value)}
        className={cn(
          'peer h-16 w-full border border-white/15 bg-[#092031] px-5 pr-14 text-lg text-white outline-none transition [color-scheme:dark] focus:border-white/45 [&::-webkit-calendar-picker-indicator]:opacity-0',
          dateValue ? 'text-white' : 'text-transparent',
        )}
      />
      {!dateValue ? (
        <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-lg text-white/45">
          {placeholder}
        </span>
      ) : null}
      <button
        type="button"
        disabled={!onChange}
        className="absolute right-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-white/65 transition hover:text-white disabled:pointer-events-none disabled:opacity-50"
        onClick={() => {
          const pickerInput = inputRef.current as
            (HTMLInputElement & { showPicker?: () => void }) | null;

          pickerInput?.focus();
          pickerInput?.showPicker?.();
        }}
        aria-label={placeholder}
      >
        <CalendarDays className="h-5 w-5" />
      </button>
    </div>
  );
}

function TextareaLike({
  placeholder,
  value = '',
  onChange,
}: {
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
}) {
  return (
    <textarea
      data-configurator-quote-input
      placeholder={placeholder}
      value={value}
      readOnly={!onChange}
      onChange={event => onChange?.(event.target.value)}
      className="h-48 resize-none border border-white/15 bg-[#092031] px-5 py-5 text-lg text-white outline-none transition placeholder:text-white/45 focus:border-white/45"
    />
  );
}

function BottomActions({
  currentStep,
  isSubmitted = false,
  isSubmitting = false,
  setCurrentStep,
  onSubmit,
  onSaveDraft,
  onBackToDashboard,
}: {
  currentStep: number;
  isSubmitted?: boolean;
  isSubmitting?: boolean;
  setCurrentStep: (step: number) => void;
  onSubmit: () => void;
  onSaveDraft: () => void;
  onBackToDashboard: () => void;
}) {
  const nextLabel =
    currentStep === 1
      ? 'Pokračovať na výber stroja'
      : currentStep === 2
        ? 'Pokračovať na parametre'
        : currentStep === 3
          ? 'Pokračovať na súhrn'
          : isSubmitted
            ? 'Uložiť zmeny'
            : 'Odoslať konfiguráciu';

  return (
    <div
      data-configurator-bottom-actions
      className="border-t border-white/10 bg-[#050505] px-4 py-4 sm:px-6 lg:px-8"
    >
      <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            className="h-12 rounded-none border-white/20 bg-white/5 px-6 !text-white hover:bg-white/10 hover:!text-white"
            onClick={() =>
              currentStep === 1 ? onBackToDashboard() : setCurrentStep(currentStep - 1)
            }
          >
            <ArrowLeft className="h-5 w-5" /> Späť
          </Button>
          {!isSubmitted ? (
            <Button
              variant="outline"
              className="h-12 rounded-none border-white/20 bg-white/5 px-6 !text-white hover:bg-white/10 hover:!text-white disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
              onClick={onSaveDraft}
            >
              {isSubmitting ? 'Ukladám...' : 'Pokračovať neskôr'}
            </Button>
          ) : null}
        </div>
        <Button
          className="h-12 rounded-none bg-[#e87500] px-6 text-white hover:bg-[#cf6800] disabled:cursor-not-allowed disabled:opacity-70"
          disabled={isSubmitting}
          onClick={() => (currentStep === 4 ? onSubmit() : setCurrentStep(currentStep + 1))}
        >
          {currentStep === 4 ? (
            isSubmitted ? (
              <Check className="h-5 w-5" />
            ) : (
              <Send className="h-5 w-5" />
            )
          ) : null}
          {currentStep === 4 && isSubmitting
            ? isSubmitted
              ? 'Ukladám...'
              : 'Odosielam...'
            : nextLabel}
          <ArrowRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

type ConfiguratorPageProps = {
  configuratorData?: RuntimeConfiguratorData | ConfiguratorData | null;
  dashboardConfigurations?: StoreDashboardConfiguration[];
  currentCustomer?: Customer | null;
  authToken?: string | null;
  locale?: string;
};

export function ConfiguratorPage({
  authToken,
  configuratorData,
  currentCustomer,
  dashboardConfigurations,
  locale,
}: ConfiguratorPageProps = {}) {
  const { t } = useTranslations();
  const { toast } = useToast();
  const router = useRouter();
  const customerAuthToken = useCustomerStore(state => state.me?.authToken);
  const storeCustomer = useCustomerStore(state => state.me?.customer);
  const customer = storeCustomer ?? currentCustomer ?? null;
  const effectiveAuthToken = customerAuthToken ?? authToken ?? null;
  const initialConfiguratorDataRef = useRef<RuntimeConfiguratorData | ConfiguratorData | null>(
    null,
  );
  if (!initialConfiguratorDataRef.current) {
    initialConfiguratorDataRef.current = configuratorData ?? getConfiguratorData();
    setConfiguratorData(initialConfiguratorDataRef.current);
  }

  const [screen, setScreen] = useState<Screen>('dashboard');
  const [flowMode, setFlowMode] = useState<FlowMode>('new');
  const [currentStep, setCurrentStep] = useState(1);
  const [maxVisitedStep, setMaxVisitedStep] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('3d');
  const [selectedHoleIndexes, setSelectedHoleIndexes] = useState<number[]>([]);
  const [activeHoleIndex, setActiveHoleIndex] = useState<number | null>(null);
  const [holeGridStepMm, setHoleGridStepMm] = useState(1);
  const [isSubmittingConfiguration, setIsSubmittingConfiguration] = useState(false);
  const [isLoadingConfigurationDetail, setIsLoadingConfigurationDetail] = useState(false);
  const [quoteCustomerData, setQuoteCustomerData] =
    useState<QuoteCustomerData>(EMPTY_QUOTE_CUSTOMER_DATA);
  const [createdConfiguration, setCreatedConfiguration] =
    useState<StoredConfiguratorConfiguration | null>(null);
  const [editingConfiguration, setEditingConfiguration] =
    useState<StoredConfiguratorConfiguration | null>(null);
  const initialConfigurationQueryHandledRef = useRef(false);
  const [config, setConfig] = useState<ConfiguratorState>(() =>
    withDefaultConfigurationName(initialConfiguratorDataRef.current!.defaults as ConfiguratorState),
  );

  const estimate = useMemo(() => calculateResolvedEstimate(config), [config]);
  const update = (patch: Partial<ConfiguratorState>) => setConfig(prev => ({ ...prev, ...patch }));
  const updatePlateSize = (patch: Partial<Pick<ConfiguratorState, 'widthMm' | 'lengthMm'>>) =>
    setConfig(prev => ({
      ...prev,
      ...patch,
      ...(shouldResetAutoHolePositions(prev) ? { holePositions: [] } : {}),
    }));

  useEffect(() => {
    document.body.classList.add('ea-configurator-active');
    return () => {
      document.body.classList.remove('ea-configurator-active');
      document.body.classList.remove('ea-configurator-flow-active');
    };
  }, []);

  useEffect(() => {
    if (!customer) return;

    setQuoteCustomerData(previous =>
      isQuoteCustomerDataTouched(previous) ? previous : getQuoteCustomerDataFromCustomer(customer),
    );
  }, [customer]);

  useEffect(() => {
    const isFlowActive = screen !== 'dashboard' && currentStep !== 5;
    document.body.classList.toggle('ea-configurator-flow-active', isFlowActive);
    return () => {
      document.body.classList.remove('ea-configurator-flow-active');
    };
  }, [screen, currentStep]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [screen, currentStep]);

  useEffect(() => {
    if (screen !== 'flow') return;
    setMaxVisitedStep(previous => Math.max(previous, currentStep));
  }, [screen, currentStep]);

  const applyStoredConfiguration = useCallback(
    (
      configuration: StoreDashboardConfiguration | null | undefined,
      { syncUrl = true }: { syncUrl?: boolean } = {},
    ) => {
      const nextConfig = normalizeStoredConfiguratorState(configuration);

      if (!nextConfig) {
        toast({
          title: t('text4099'),
          description: 'Uložená konfigurácia nemá platný payload produktu.',
          variant: 'destructive',
        });
        return false;
      }

      const storedConfiguration = configuration as StoredConfiguratorConfiguration;
      const storedCustomerData = getQuoteCustomerDataFromStoredConfiguration(storedConfiguration);
      setConfig(nextConfig);
      setEditingConfiguration(storedConfiguration);
      setCreatedConfiguration(storedConfiguration);
      setFlowMode(isDraftConfiguration(storedConfiguration) ? 'draft' : 'submitted');
      setQuoteCustomerData(
        isQuoteCustomerDataTouched(storedCustomerData)
          ? storedCustomerData
          : customer
            ? getQuoteCustomerDataFromCustomer(customer)
            : storedCustomerData,
      );
      setSelectedHoleIndexes([]);
      setActiveHoleIndex(null);
      setScreen('flow');
      setCurrentStep(3);
      setMaxVisitedStep(4);
      setViewMode('3d');

      if (syncUrl) {
        setConfigurationQueryKey(getConfigurationLookupKey(storedConfiguration));
      }

      return true;
    },
    [customer, toast],
  );

  const openStoredConfiguration = useCallback(
    async (configuration: StoreDashboardConfiguration) => {
      const configurationKey = getConfigurationLookupKey(configuration);

      setIsLoadingConfigurationDetail(true);
      if (configurationKey) setConfigurationQueryKey(configurationKey);

      try {
        const detail = configurationKey
          ? await loadStoreConfigurationDetail({
              authToken: effectiveAuthToken,
              configurationKey,
              locale,
            })
          : null;

        applyStoredConfiguration(detail ?? configuration);
      } catch (error) {
        console.error(error);
        if (!applyStoredConfiguration(configuration)) {
          toast({
            title: t('text4097'),
            description: 'Skontrolujte prosím prístup alebo skúste stránku obnoviť.',
            variant: 'destructive',
          });
        }
      } finally {
        setIsLoadingConfigurationDetail(false);
      }
    },
    [applyStoredConfiguration, effectiveAuthToken, locale, toast],
  );

  useEffect(() => {
    if (initialConfigurationQueryHandledRef.current) return;

    const configurationKey = getInitialConfigurationQueryKey();
    if (!configurationKey) {
      initialConfigurationQueryHandledRef.current = true;
      return;
    }

    initialConfigurationQueryHandledRef.current = true;
    const matchingConfiguration = dashboardConfigurations?.find(configuration => {
      const key = getConfigurationLookupKey(configuration);
      return key === configurationKey || String(configuration.id ?? '') === configurationKey;
    });

    void openStoredConfiguration(
      matchingConfiguration ?? ({ token: configurationKey } as StoreDashboardConfiguration),
    );
  }, [dashboardConfigurations, openStoredConfiguration]);

  useEffect(() => {
    setSelectedHoleIndexes(previous => {
      const next = previous.filter(index => index < config.holeCount);
      setActiveHoleIndex(activeIndex =>
        activeIndex !== null && next.includes(activeIndex) ? activeIndex : (next[0] ?? null),
      );
      return next.length === previous.length ? previous : next;
    });
  }, [config.holeCount]);

  const startFlow = () => {
    setFlowMode('new');
    setEditingConfiguration(null);
    setCreatedConfiguration(null);
    setQuoteCustomerData(
      customer ? getQuoteCustomerDataFromCustomer(customer) : EMPTY_QUOTE_CUSTOMER_DATA,
    );
    setConfig(
      withDefaultConfigurationName(
        initialConfiguratorDataRef.current!.defaults as ConfiguratorState,
      ),
    );
    setConfigurationQueryKey();
    setScreen('flow');
    setCurrentStep(1);
    setMaxVisitedStep(1);
  };

  const returnToDashboard = () => {
    setConfigurationQueryKey();
    setScreen('dashboard');
    setCurrentStep(1);
    setMaxVisitedStep(1);
  };

  const saveDraftAndExit = async () => {
    if (isSubmittingConfiguration) return;

    setIsSubmittingConfiguration(true);
    try {
      const persistedConfig = withDefaultConfigurationName(config);
      const saved = editingConfiguration
        ? await updateStoreConfiguration({
            authToken: effectiveAuthToken,
            configuration: editingConfiguration,
            config: persistedConfig,
            customerData: quoteCustomerData,
            locale,
            status: flowMode === 'submitted' ? undefined : STORE_CONFIGURATION_STATUS_DRAFT,
            currentStep,
          })
        : await createStoreConfiguration({
            authToken: effectiveAuthToken,
            config: persistedConfig,
            customerData: quoteCustomerData,
            locale,
            status: STORE_CONFIGURATION_STATUS_DRAFT,
            currentStep,
          });

      if (saved) {
        setCreatedConfiguration(saved);
        setEditingConfiguration(saved);
      }

      if (flowMode === 'new') setFlowMode('draft');
      setConfig(persistedConfig);

      toast({
        title: editingConfiguration ? 'Zmeny boli uložené' : 'Konfigurácia je uložená',
        description:
          flowMode === 'submitted'
            ? 'Odoslaná konfigurácia bola aktualizovaná.'
            : 'Môžete sa k nej vrátiť neskôr.',
        variant: 'success',
      });
      router.refresh();
      returnToDashboard();
    } catch (error) {
      console.error(error);
      toast({
        title: t('text4100'),
        description: 'Skúste to prosím znova.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingConfiguration(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      await setAuthToken({ authToken: '' });
    } catch (error) {
      console.error(error);
    } finally {
      initCustomerStore(null);
      router.refresh();
    }
  }, [router]);

  const selectReachableStep = (step: number) => {
    if (step === 5) return;

    const maxReachableStep = Math.min(CONFIGURATOR_STEPS.length, maxVisitedStep + 1);
    if (step > maxReachableStep) return;

    setCurrentStep(step);
  };

  const submitConfiguration = async () => {
    if (isSubmittingConfiguration) return;

    setIsSubmittingConfiguration(true);
    try {
      const persistedConfig = withDefaultConfigurationName(config);
      const created = editingConfiguration
        ? await updateStoreConfiguration({
            authToken: effectiveAuthToken,
            configuration: editingConfiguration,
            config: persistedConfig,
            customerData: quoteCustomerData,
            locale,
            status: flowMode === 'submitted' ? undefined : STORE_CONFIGURATION_STATUS_CREATED,
            currentStep: 4,
          })
        : await createStoreConfiguration({
            authToken: effectiveAuthToken,
            config: persistedConfig,
            customerData: quoteCustomerData,
            locale,
            status: STORE_CONFIGURATION_STATUS_CREATED,
            currentStep: 4,
          });
      setCreatedConfiguration(created);
      if (created) setEditingConfiguration(created);
      setConfig(persistedConfig);
      if (flowMode === 'submitted') {
        toast({
          title: 'Zmeny boli uložené',
          description: 'Odoslaná konfigurácia bola aktualizovaná.',
          variant: 'success',
        });
      }
      router.refresh();
      setCurrentStep(5);
    } catch (error) {
      console.error(error);
      toast({
        title:
          flowMode === 'submitted'
            ? t('text4100')
            : t('text4098'),
        description: 'Skúste to prosím znova.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingConfiguration(false);
    }
  };

  return (
    <main
      data-configurator-main
      data-configurator-flow-active={screen !== 'dashboard' && currentStep !== 5 ? 'true' : 'false'}
      className="lisonex-configurator flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[#071d2b] text-white"
    >
      <LisonexConfiguratorStyles />
      <Toaster />
      <LisonexTopBar
        leftLabel={
          screen === 'dashboard' ? undefined : currentStep === 5 ? 'Domov' : 'Uložiť a ukončiť'
        }
        onLeftClick={
          screen === 'dashboard'
            ? undefined
            : currentStep === 5
              ? returnToDashboard
              : saveDraftAndExit
        }
        onLogout={logout}
        userName={getCustomerDisplayName(customer)}
      />
      {isLoadingConfigurationDetail ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#071d2b]/75 text-sm font-semibold uppercase tracking-[0.12em] text-white">
          Načítavam konfiguráciu...
        </div>
      ) : null}
      {screen === 'dashboard' ? (
        <Dashboard
          onStart={startFlow}
          onOpenConfiguration={openStoredConfiguration}
          authToken={effectiveAuthToken}
          dashboardConfigurations={dashboardConfigurations}
          locale={locale}
        />
      ) : currentStep === 5 ? (
        <SuccessScreen
          config={config}
          createdConfiguration={createdConfiguration}
          estimate={estimate}
          isEditing={flowMode === 'submitted'}
          onNew={startFlow}
        />
      ) : (
        <>
          <div
            data-configurator-flow-shell
            className="mx-auto grid w-full max-w-[1660px] gap-8 px-5 py-10 sm:px-8 lg:grid-cols-[360px_minmax(0,1fr)] lg:py-12"
          >
            <Stepper
              currentStep={currentStep}
              isSubmitted={flowMode === 'submitted'}
              maxVisitedStep={maxVisitedStep}
              onStepSelect={selectReachableStep}
            />
            <div
              data-configurator-flow-content={currentStep === 3 ? 'parameters' : 'steps'}
              className="w-full min-w-0"
            >
              {currentStep === 1 && <ProductStep config={config} update={update} />}
              {currentStep === 2 && <MachineStep config={config} update={update} />}
              {currentStep === 3 && (
                <ParametersStep
                  config={config}
                  estimate={estimate}
                  update={update}
                  updatePlateSize={updatePlateSize}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  selectedHoleIndexes={selectedHoleIndexes}
                  activeHoleIndex={activeHoleIndex}
                  onHoleSelectionChange={(indexes, activeIndex) => {
                    setSelectedHoleIndexes(indexes);
                    setActiveHoleIndex(activeIndex);
                  }}
                  holeGridStepMm={holeGridStepMm}
                  setHoleGridStepMm={setHoleGridStepMm}
                />
              )}
              {currentStep === 4 && (
                <SummaryStep
                  config={config}
                  estimate={estimate}
                  isSubmitted={flowMode === 'submitted'}
                  onConfigurationNameChange={configurationName => update({ configurationName })}
                  onEdit={() => setCurrentStep(3)}
                  quoteCustomerData={quoteCustomerData}
                  onQuoteCustomerDataChange={patch =>
                    setQuoteCustomerData(previous => ({ ...previous, ...patch }))
                  }
                />
              )}
            </div>
          </div>
          <BottomActions
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            isSubmitted={flowMode === 'submitted'}
            isSubmitting={isSubmittingConfiguration}
            onSubmit={submitConfiguration}
            onSaveDraft={saveDraftAndExit}
            onBackToDashboard={returnToDashboard}
          />
        </>
      )}
    </main>
  );
}
